<template>
  <div
    class="dashboard"
    :class="{ 'panel-mode': isPanelMode, 'dashboard--llm': effectiveLlmEnabled, 'dashboard--editing': editMode }"
  >
    <!-- Panel mode: Add Widget (loaded in Optix parent's side panel) -->
    <div
      v-if="isAddWidgetPanelMode"
      class="panel-mode-container"
    >
      <AddWidgetPanel
        v-if="!initialLoading"
        :is-open="true"
        :embedded="true"
        :target-section-id="addWidgetPanelTargetSectionId"
        :sections="sections"
        :available-widgets="allWidgetsForAddPanel"
        :restricted-widget-ids="restrictedWidgetIds"
        :existing-widgets="allExistingWidgets"
        @update:is-open="handlePanelClose"
        @add-widget="handleAddWidget"
      />
      <div
        v-else
        class="panel-loading"
      >
        <SkeletonLoader type="dashboard" />
      </div>
    </div>
    <!-- Panel mode: Widget Detail (automations badge click - shows widget title + best practices) -->
    <div
      v-else-if="isWidgetDetailPanelMode"
      class="panel-mode-container"
    >
      <WidgetDetailPanel
        :title="widgetDetailPanelTitle"
        :widget-type="widgetDetailPanelType"
        :best-practice-type="widgetDetailBestPracticeType"
        @close="handleWidgetDetailPanelClose"
      />
    </div>
    <!-- Panel mode: View All Actions (from Tasks Handled Automatically widget) -->
    <div
      v-else-if="isViewAllActionsPanelMode"
      class="panel-mode-container"
    >
      <ActionsViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: View All Time Saved (from Time Saved by Automations widget) -->
    <div
      v-else-if="isViewAllTimeSavedPanelMode"
      class="panel-mode-container"
    >
      <TimeSavedViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: View All Top Bookers (from Top Bookers widget) -->
    <div
      v-else-if="isViewAllTopBookersPanelMode"
      class="panel-mode-container"
    >
      <TopBookersViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: View All Revenue Collected (from Revenue collected widget) -->
    <div
      v-else-if="isViewAllRevenueCollectedPanelMode"
      class="panel-mode-container"
    >
      <RevenueCollectedViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: View All Conversion Accounts (from conversion widgets) -->
    <div
      v-else-if="isViewAllConversionAccountsPanelMode"
      class="panel-mode-container"
    >
      <ConversionAccountsViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: View All Support Chat (from Support Chat widget) -->
    <div
      v-else-if="isViewAllSupportChatPanelMode"
      class="panel-mode-container"
    >
      <SupportChatViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: View All Best Practices (from Best Practices Automated widget) -->
    <div
      v-else-if="isViewAllBestPracticesPanelMode"
      class="panel-mode-container"
    >
      <BestPracticesViewAllPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: Discover Groups (from Public Group Conversations widget) -->
    <div
      v-else-if="isDiscoverGroupsPanelMode"
      class="panel-mode-container"
    >
      <DiscoverGroupsPanel
        @close="handleViewAllPanelClose"
      />
    </div>
    <!-- Panel mode: Inbox Conversation (from Latest Messages widget click) -->
    <div
      v-else-if="isInboxConversationPanelMode"
      class="panel-mode-container panel-mode-container--iframe"
    >
      <div class="inbox-conversation-panel">
        <button
          class="inbox-conversation-close"
          aria-label="Close"
          @click="handleInboxConversationClose"
        >
          ×
        </button>
        <iframe
          v-if="inboxConversationIframeSrc"
          :src="inboxConversationIframeSrc"
          class="inbox-conversation-iframe"
          title="Conversation"
        />
      </div>
    </div>
    <!-- Panel mode: Dashboard Settings (owner-only, opened from edit-mode gear) -->
    <div
      v-else-if="isSettingsPanelMode"
      class="panel-mode-container"
    >
      <DashboardSettingsPanel
        @close="handleSettingsPanelClose"
      />
    </div>
    <template v-else>
      <!-- Mobile burger menu - top left, calls Optix ToggleOptixMenu when clicked. Show when viewport < 500px OR menu closed, and add widget panel is closed. -->
      <div
        v-if="!initialLoading && !isShowingEmptyState && isInIframe && showBurger && !showAddWidgetPanel"
        class="header-left"
      >
        <button
          type="button"
          class="burger-btn"
          aria-label="Open menu"
          @click.prevent="toggleOptixMenu"
          @mousedown.prevent
        >
          <svg
            class="burger-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              x1="4"
              y1="6"
              x2="20"
              y2="6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="4"
              y1="12"
              x2="20"
              y2="12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="4"
              y1="18"
              x2="20"
              y2="18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
      <!-- Full-width blur bar backdrop -->
      <div
        v-if="!initialLoading && !isShowingEmptyState && editMode"
        class="header-backdrop"
      />
      <!-- Edit button fixed at top right (stays visible when scrolling) -->
      <div
        v-if="!initialLoading && !isShowingEmptyState"
        class="header-right"
      >
        <div
          v-if="orgId"
          class="org-info"
        >
          Organization: {{ orgId }}
        </div>
        <div
          v-if="isSaving"
          class="save-indicator"
        >
          Saving...
        </div>
        <div
          v-if="showTrialToggle"
          class="trial-mode-wrapper"
        >
          <label
            class="trial-mode-toggle"
            :class="{ 'trial-mode-toggle--on': trialMode }"
          >
            <span class="trial-mode-label">Trial mode</span>
            <span class="trial-mode-switch">
              <input
                v-model="trialMode"
                type="checkbox"
                class="trial-mode-input"
                @change="onTrialModeChange"
              >
              <span class="trial-mode-track" />
              <span class="trial-mode-thumb" />
            </span>
          </label>
          <span class="trial-mode-tooltip">Preview dashboard with sample data</span>
        </div>
        <div 
          v-if="token && isInIframe && !editMode && !showBurgerIcon"
          ref="openNewTabBtnRef"
          class="open-new-tab-wrapper"
          @mouseenter="showOpenNewTabTooltip = true"
          @mouseleave="showOpenNewTabTooltip = false"
        >
          <button 
            class="open-new-tab-btn" 
            @click.prevent="openInNewTab" 
            @mousedown.prevent
          >
            <svg
              class="open-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <polyline
                points="15 3 21 3 21 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <line
                x1="10"
                y1="14"
                x2="21"
                y2="3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <Teleport to="body">
            <div
              v-if="showOpenNewTabTooltip"
              ref="openNewTabTooltipRef"
              class="open-new-tab-tooltip"
              :style="openNewTabTooltipStyle"
            >
              Open dashboard in new tab
            </div>
          </Teleport>
        </div>
        <div
          v-if="editMode && showEditControls"
          class="edit-actions"
        >
          <button
            v-if="userRole === 'OWNER'"
            class="settings-btn"
            aria-label="Dashboard settings"
            @click.prevent="openSettingsPanel"
            @mousedown.prevent
          >
            <svg
              class="settings-btn-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            class="cancel-btn"
            @click.prevent="cancelEdit"
            @mousedown.prevent
          >
            Cancel
          </button>
          <button
            class="save-btn"
            @click.prevent="saveEdit"
            @mousedown.prevent
          >
            Save
          </button>
        </div>
        <div
          v-if="showEditControls && !editMode"
          ref="editButtonRef"
          class="edit-dashboard-wrapper"
          @mouseenter="showEditTooltip = true"
          @mouseleave="showEditTooltip = false"
        >
          <button
            class="edit-dashboard-btn"
            @click.prevent="toggleEditMode"
            @mousedown.prevent
          >
            <span class="edit-dashboard-text">Edit</span>
            <svg
              class="edit-dashboard-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <Teleport to="body">
            <div
              v-if="showEditTooltip"
              ref="editTooltipRef"
              class="edit-dashboard-tooltip"
              :style="editTooltipStyle"
            >
              Customize your view
            </div>
          </Teleport>
        </div>
      </div>
    
      <!-- Rate limit error (429) with nice UI -->
      <div
        v-if="error && isRateLimitError"
        class="rate-limit-error"
      >
        <div class="rate-limit-error-content">
          <svg
            class="rate-limit-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M12 6V12L16 14"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <h2 class="rate-limit-title">
            Too many requests
          </h2>
          <p class="rate-limit-description">
            Please retry after 30 seconds
          </p>
        </div>
      </div>
    
      <!-- Other errors -->
      <div
        v-else-if="error"
        class="error"
      >
        Error: {{ error }}
      </div>
    
      <!-- Error state for missing token in fullscreen mode -->
      <div
        v-if="!initialLoading && isFullscreenMode && !token && tokenError"
        class="error-state"
      >
        <div class="error-state-content">
          <svg
            class="error-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="2"
            />
            <line
              x1="12"
              y1="8"
              x2="12"
              y2="12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="12"
              y1="16"
              x2="12.01"
              y2="16"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <h2 class="error-state-title">
            Invalid or Expired Link
          </h2>
          <p class="error-state-description">
            This dashboard link is invalid or has expired. Dashboard links cannot be shared directly.
          </p>
          <p class="error-state-description-secondary">
            Please access the dashboard from within the application to view your data.
          </p>
        </div>
      </div>
    
      <div
        v-if="!(isFullscreenMode && !token && tokenError)"
        class="dashboard-content-wrapper"
      >
        <div
          ref="dashboardContentRef"
          class="dashboard-content"
          :class="{ 'panel-open': showAddWidgetPanel }"
        >
          <div
            v-if="isFullscreenMode && !initialLoading && orgName"
            class="fullscreen-header"
          >
            <img
              v-if="orgLogoUrl"
              :src="orgLogoUrl"
              :alt="orgName"
              class="fullscreen-header-avatar"
            >
            <div
              v-else
              class="fullscreen-header-avatar fullscreen-header-avatar--placeholder"
            >
              {{ orgName.charAt(0).toUpperCase() }}
            </div>
            <span class="fullscreen-header-name">{{ orgName }}</span>
          </div>
          <div
            v-if="!initialLoading && !isShowingEmptyState && !isFullscreenMode"
            ref="dashboardHeaderRef"
            class="dashboard-header"
            :class="{ 'dashboard-header--llm': effectiveLlmEnabled }"
          >
            <div class="header-top">
              <h1 class="header-title">
                {{ greetingTitle }}
              </h1>
            </div>
            <div
              v-if="!isFullscreenMode && !isShowingEmptyState"
              class="header-bottom"
            >
              <p class="dashboard-subtitle">
                Stay on top of workplace activity and automate operations.
              </p>
              <div class="dashboard-links">
                <div class="dashboard-link-container">
                  <a
                    href="https://support.optixapp.com/en/articles/14415846-what-is-the-dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="dashboard-link"
                  >
                    <svg
                      class="link-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 19.5C4 18.837 4.263 18.201 4.732 17.732C5.201 17.263 5.837 17 6.5 17H20"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M6.5 2H20V22H6.5C5.837 22 5.201 21.737 4.732 21.268C4.263 20.799 4 20.163 4 19.5V4.5C4 3.837 4.263 3.201 4.732 2.732C5.201 2.263 5.837 2 6.5 2Z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span class="link-text-desktop">Get help with your dashboard</span>
                    <span class="link-text-mobile">Get help</span>
                  </a>
                </div>
              </div>
              <div
                v-if="trialMode"
                class="trial-mode-pill-wrapper"
              >
                <span class="trial-mode-pill">Trial mode: showing demo data</span>
                <span class="trial-mode-pill-tooltip">See your data by turning off the switch at the top right</span>
              </div>
            </div>
            <div
              v-if="effectiveLlmEnabled"
              class="optix-prompt-wrapper"
            >
              <div class="optix-prompt">
                <textarea
                  ref="optixPromptInput"
                  v-model="optixPromptText"
                  class="optix-prompt-input"
                  placeholder="Ask Optix anything"
                  rows="1"
                  @keydown.enter="handleOptixKeydown"
                  @input="handleOptixInput"
                />
                <div class="optix-prompt-footer">
                  <button
                    type="button"
                    class="optix-prompt-attach"
                    title="Attach"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 5V19M5 12H19"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  <span class="optix-prompt-model">Optix</span>
                  <button
                    type="button"
                    class="optix-prompt-send"
                    title="Send"
                    @click="handleOptixSubmit"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 19V5M5 12L12 5L19 12"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="optix-prompt-suggestions">
                <button
                  type="button"
                  class="optix-suggestion-btn"
                  @click="handleOptixSuggestion('insights')"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <line
                      x1="18"
                      y1="20"
                      x2="18"
                      y2="10"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="12"
                      y1="20"
                      x2="12"
                      y2="4"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="6"
                      y1="20"
                      x2="6"
                      y2="14"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Insights
                </button>
                <button
                  type="button"
                  class="optix-suggestion-btn"
                  @click="handleOptixSuggestion('automations')"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Automations
                </button>
                <button
                  type="button"
                  class="optix-suggestion-btn"
                  @click="handleOptixSuggestion('bookings')"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="16"
                      y1="2"
                      x2="16"
                      y2="6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="8"
                      y1="2"
                      x2="8"
                      y2="6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="3"
                      y1="10"
                      x2="21"
                      y2="10"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Bookings & assignments
                </button>
                <button
                  type="button"
                  class="optix-suggestion-btn"
                  @click="handleOptixSuggestion('tasks')"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 11l3 3L22 4"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Tasks
                </button>
                <button
                  type="button"
                  class="optix-suggestion-btn"
                  @click="handleOptixSuggestion('suggest')"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18h6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M10 22h4"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Suggest
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="effectiveLlmEnabled && !initialLoading && (sections && sections.length > 0 && (hasWidgets || editMode))"
            class="sections-spacer"
            :style="sectionsSpacerStyle"
          />
          <div
            v-if="isDashboardRateLimitError"
            class="rate-limit-error"
          >
            <div class="rate-limit-error-content">
              <svg
                class="rate-limit-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path
                  d="M12 6V12L16 14"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <h2 class="rate-limit-title">
                Too many requests
              </h2>
              <p class="rate-limit-description">
                Please retry after 30 seconds
              </p>
            </div>
          </div>
          <div
            v-else-if="(initialLoading && showSkeletonWhileLoading) || trialModeRefreshing"
            class="empty-state empty-state--loading"
          >
            <div class="empty-state-skeleton">
              <SkeletonLoader type="dashboard" />
            </div>
          </div>
          <div
            v-else-if="!initialLoading && !trialModeRefreshing && isShowingEmptyState"
            class="empty-state"
          >
            <div
              class="empty-state-content"
              :class="{ 'empty-state-content--delayed': !emptyStateVisible }"
            >
              <h2 class="empty-state-title">
                {{ emptyStateGreeting }}
              </h2>
              <p class="empty-state-description">
                Choose the categories you want on your dashboard. This view is just for you, you can make changes at anytime.
              </p>
              <div class="empty-state-categories">
                <div class="empty-state-category-btn empty-state-category-btn--selected empty-state-category-btn--locked">
                  <span
                    class="category-btn-drag-handle category-btn-drag-handle--disabled"
                    aria-hidden="true"
                  >
                    <span class="material-icons">drag_indicator</span>
                  </span>
                  <span class="category-btn-title">Your Focus</span>
                  <span class="category-btn-check-wrapper">
                    <span
                      class="category-btn-check material-icons"
                      aria-hidden="true"
                    >check</span>
                    <span class="category-btn-locked-tooltip">Cannot be removed</span>
                  </span>
                </div>
                <div
                  ref="setupCategoryListRef"
                  class="empty-state-category-list"
                >
                  <div
                    v-for="group in orderedCategoryGroups"
                    :key="group.section"
                    :data-section="group.section"
                    class="empty-state-category-item"
                  >
                    <button
                      type="button"
                      class="empty-state-category-btn"
                      :class="{
                        'empty-state-category-btn--selected': selectedCategories.includes(group.section),
                        'empty-state-category-btn--reorderable': selectedCategories.includes(group.section)
                      }"
                      @click="toggleCategory(group.section)"
                    >
                      <span
                        class="category-btn-drag-handle"
                        aria-hidden="true"
                      >
                        <span class="material-icons">drag_indicator</span>
                      </span>
                      <span class="category-btn-title">{{ group.displayName }}</span>
                      <span
                        class="category-btn-check material-icons"
                        :class="{ 'category-btn-check--empty': !selectedCategories.includes(group.section) }"
                        aria-hidden="true"
                      >{{ selectedCategories.includes(group.section) ? 'check' : '' }}</span>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  class="empty-state-create-btn"
                  :disabled="selectedCategories.length === 0"
                  @click="createDashboardFromCategories"
                >
                  Create dashboard
                </button>
              </div>
            </div>
          </div>
          <div
            v-else-if="!trialModeRefreshing && sections && sections.length > 0 && (hasWidgets || editMode)"
            class="sections-container"
          >
            <div
              v-if="editMode && showEditControls"
              class="add-section-zone"
              @click="addSection"
            >
              <svg
                class="add-section-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 4V16M4 10H16"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              <span>Add section</span>
            </div>
            <div
              ref="sectionsListRef"
              class="sections-list"
            >
              <DashboardSection
                v-for="(section, index) in sections"
                :key="section.id"
                :section="section"
                :columns="columns"
                :row-height="rowHeight"
                :gap="8"
                :edit-mode="editMode"
                :section-index="index"
                :total-sections="sections.length"
                :available-widgets="availableWidgets"
                @update:section="handleSectionUpdate"
                @delete:section="handleSectionDelete"
                @update:widgets="handleWidgetsUpdate"
                @add-widget="handleAddWidget"
                @open-settings="handleWidgetSettingsOpen"
                @move-section="handleSectionMove"
                @remove-widget="handleRemoveWidget"
              />
            </div>
          </div>
        </div>
        <div
          v-if="effectiveLlmEnabled && (initialLoading || (sections && sections.length > 0 && (hasWidgets || editMode)))"
          class="scroll-fade-overlay"
          :class="{ 'scroll-fade-overlay--hidden': !showScrollChevron }"
          aria-hidden="true"
        />
        <div
          v-if="effectiveLlmEnabled && (initialLoading || (sections && sections.length > 0 && (hasWidgets || editMode)))"
          class="scroll-chevron"
          :class="{ 'scroll-chevron--hidden': !showScrollChevron }"
          aria-hidden="true"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
    
      <AddWidgetPanel
        :is-open="showAddWidgetPanel"
        :target-section-id="addWidgetPanelTargetSectionId"
        :sections="sections"
        :available-widgets="allWidgetsForAddPanel"
        :restricted-widget-ids="restrictedWidgetIds"
        :existing-widgets="allExistingWidgets"
        @update:is-open="showAddWidgetPanel = $event"
        @add-widget="handleAddWidget"
      />

      <!-- Inline fallback for the settings panel (non-iframe / local dev). In Optix it opens as a native side panel. -->
      <div
        v-if="showSettingsPanel"
        class="settings-panel-overlay"
      >
        <DashboardSettingsPanel @close="showSettingsPanel = false" />
      </div>

      <!-- Edit tip callout (tour point) - shows after creating dashboard from empty state -->
      <Teleport to="body">
        <Transition name="edit-tip-fade">
          <div
            v-if="(showEditTipCallout || forceShowEditTip) && !isShowingEmptyState"
            ref="editTipCalloutRef"
            class="edit-tip-callout"
            :style="editTipCalloutStyle"
            role="status"
          >
            <p class="edit-tip-title">
              Customize your dashboard
            </p>
            <p class="edit-tip-message">
              The items you choose only affect your view.
            </p>
            <div class="edit-tip-actions">
              <button
                type="button"
                class="edit-tip-edit-btn"
                @click="dismissEditTip(); toggleEditMode();"
              >
                Customize
              </button>
              <button
                type="button"
                class="edit-tip-got-it-link"
                @click="dismissEditTip"
              >
                Later
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </template>

    <!-- Global Snackbar (rendered in all modes, incl. side-panel iframes) -->
    <Transition name="snackbar">
      <div
        v-if="showSnackbar"
        class="snackbar"
        :class="{ 'snackbar-wiggle': snackbarWiggleKey }"
      >
        <span class="snackbar-message">{{ snackbarMessage }}</span>
        <button
          v-if="snackbarUndoAction"
          class="snackbar-undo"
          @click="handleSnackbarUndo"
        >
          Undo
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, markRaw, nextTick, watch, provide } from 'vue'
import { validateIframeParams, getTokenFromURL, storeTokenForSession, getTrialModeFromURL } from '../utils/tokenHandler.js'
import Sortable from 'sortablejs'
import DashboardSection from '../components/DashboardSection.vue'
import AddWidgetPanel from '../components/AddWidgetPanel.vue'
import DashboardSettingsPanel from '../components/DashboardSettingsPanel.vue'
import TimeSavedWidget from '../components/TimeSavedWidget.vue'
import UpcomingBookingsWidget from '../components/UpcomingBookingsWidget.vue'
import UpcomingToursWidget from '../components/UpcomingToursWidget.vue'
import CompletedToursWidget from '../components/CompletedToursWidget.vue'
import PlansStartingSoonWidget from '../components/PlansStartingSoonWidget.vue'
import PlansEndingSoonWidget from '../components/PlansEndingSoonWidget.vue'
import AssignmentsEndingSoonWidget from '../components/AssignmentsEndingSoonWidget.vue'
import AssignmentsStartingSoonWidget from '../components/AssignmentsStartingSoonWidget.vue'
import CustomAutomationsWidget from '../components/CustomAutomationsWidget.vue'
import RecentAutomationEnrollmentsWidget from '../components/RecentAutomationEnrollmentsWidget.vue'
import MostPopularAutomationsWidget from '../components/MostPopularAutomationsWidget.vue'
import AutomationEnrolmentsWidget from '../components/AutomationEnrolmentsWidget.vue'
import CheckInsWidget from '../components/CheckInsWidget.vue'
import CheckedInWidget from '../components/CheckedInWidget.vue'
import ActionsThisPeriodWidget from '../components/ActionsThisPeriodWidget.vue'
import RevenueCollectedWidget from '../components/RevenueCollectedWidget.vue'
import UnpaidInvoicesWidget from '../components/UnpaidInvoicesWidget.vue'
import InvoicesWidget from '../components/InvoicesWidget.vue'
import TopAccountsByRevenueWidget from '../components/TopAccountsByRevenueWidget.vue'
import PlanRevenueWidget from '../components/PlanRevenueWidget.vue'
import TopBookersWidget from '../components/TopBookersWidget.vue'
import TasksWidget from '../components/TasksWidget.vue'
import LatestMessagesWidget from '../components/LatestMessagesWidget.vue'
import ManualVsAutomatedWidget from '../components/ManualVsAutomatedWidget.vue'
import ConvertedLeadsFromToursWidget from '../components/ConvertedLeadsFromToursWidget.vue'
import ConversionRateFromToursWidget from '../components/ConversionRateFromToursWidget.vue'
import ConvertedLeadsFromInquiriesWidget from '../components/ConvertedLeadsFromInquiriesWidget.vue'
import ConversionRateFromInquiriesWidget from '../components/ConversionRateFromInquiriesWidget.vue'
import ConvertedLeadsFromDropinsWidget from '../components/ConvertedLeadsFromDropinsWidget.vue'
import ConversionRateFromDropinsWidget from '../components/ConversionRateFromDropinsWidget.vue'
import TourPassPlanPurchaseWidget from '../components/TourPassPlanPurchaseWidget.vue'
import DropinRepeatBookingWidget from '../components/DropinRepeatBookingWidget.vue'
import DropinPassPlanPurchaseWidget from '../components/DropinPassPlanPurchaseWidget.vue'
import CreditOwedWidget from '../components/CreditOwedWidget.vue'
import PublicGroupConversationsWidget from '../components/PublicGroupConversationsWidget.vue'
import SupportChatWidget from '../components/SupportChatWidget.vue'
import SkeletonLoader from '../components/SkeletonLoader.vue'
import WidgetDetailPanel from '../components/WidgetDetailPanel.vue'
import ActionsViewAllPanel from '../components/ActionsViewAllPanel.vue'
import TimeSavedViewAllPanel from '../components/TimeSavedViewAllPanel.vue'
import TopBookersViewAllPanel from '../components/TopBookersViewAllPanel.vue'
import RevenueCollectedViewAllPanel from '../components/RevenueCollectedViewAllPanel.vue'
import ConversionAccountsViewAllPanel from '../components/ConversionAccountsViewAllPanel.vue'
import DiscoverGroupsPanel from '../components/DiscoverGroupsPanel.vue'
import SupportChatViewAllPanel from '../components/SupportChatViewAllPanel.vue'
import BestPracticesViewAllPanel from '../components/BestPracticesViewAllPanel.vue'
import { dataFetcher } from '../services/dataFetcher.js'
import { provideDemoMode } from '../composables/useDemoMode.js'
import { useDashboardStorage } from '../composables/useDashboardStorage.js'
import { useSnackbar } from '../composables/useSnackbar.js'
import { isRateLimitError as checkIsRateLimitError } from '../utils/errorUtils.js'
import { refreshAllWidgetData, useGraphQLData } from '../composables/useWidgetData.js'
import { getOrganizationDetails } from '../services/optixApi.js'
import { pushAdminSidePanel, popAdminSidePanel, requestParentSize, isInOptixIframe, toggleOptixMenu, useOptixMenuState } from '../composables/useOptixPanel.js'
import { getWidgetTypeFromTitle, clearInsightDismissedOnLoad } from '../utils/widgetBestPractices.js'
import { provideAutomationsFooter } from '../composables/useAutomationsFooter.js'
import { fetchBestPractices } from '../composables/useBestPracticesData.js'
import { getDemoBestPracticesData } from '../services/demoData.js'
import { useUserRole } from '../composables/useUserRole.js'
import { canViewWidget } from '../utils/roles.js'

const DASHBOARD_BROADCAST_CHANNEL = 'dashboard-add-widget'

const initialLoading = ref(true) // Start with loading true to load dashboard config
const error = ref(null)
const tokenError = ref(false) // Error state for missing token in fullscreen mode

// Check if error is a 429 rate limit error
const isRateLimitError = computed(() => {
  if (error.value && checkIsRateLimitError(error.value)) return true
  if (dashboardError.value && checkIsRateLimitError(dashboardError.value)) return true
  return false
})

// Check if dashboard failed to load due to rate limit
const isDashboardRateLimitError = computed(() => {
  return dashboardError.value && checkIsRateLimitError(dashboardError.value)
})
const orgId = ref(null)
const token = ref(null)
const windowWidth = ref(window.innerWidth)
const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 100)
const editMode = ref(false)
const showAddWidgetPanel = ref(false)
const showSettingsPanel = ref(false)
/** Show callout tip pointing at edit button after user creates dashboard from empty state.
 * While building: localStorage.setItem('dashboard-show-edit-tip', '1') to persist and see it always. */
const showEditTipCallout = ref(false)
const forceShowEditTip = ref(typeof window !== 'undefined' && localStorage.getItem('dashboard-show-edit-tip') === '1')

const dismissEditTip = () => {
  showEditTipCallout.value = false
  if (forceShowEditTip.value) {
    localStorage.removeItem('dashboard-show-edit-tip')
    forceShowEditTip.value = false
  }
}
const sectionsListRef = ref(null)
const editButtonRef = ref(null)
const editTipCalloutRef = ref(null)
const editTipCalloutStyle = ref(null)
const editTooltipRef = ref(null)
const showEditTooltip = ref(false)
const editTooltipPosition = ref(null)

const editTooltipStyle = computed(() => {
  if (!editTooltipPosition.value) return {}
  const { left, top } = editTooltipPosition.value
  return {
    left: `${left}px`,
    top: `${top}px`,
    transform: 'translateX(-50%)'
  }
})

function updateEditTooltipPosition() {
  if (!editButtonRef.value || !editTooltipRef.value) return
  const rect = editButtonRef.value.getBoundingClientRect()
  const tip = editTooltipRef.value
  const tipWidth = tip.offsetWidth || 140
  const gap = 8
  const padding = 12
  const vw = window.innerWidth
  let left = rect.left + rect.width / 2
  const minLeft = padding + tipWidth / 2
  const maxLeft = vw - padding - tipWidth / 2
  left = Math.max(minLeft, Math.min(maxLeft, left))
  editTooltipPosition.value = {
    left,
    top: rect.bottom + gap
  }
}

watch(showEditTooltip, (visible) => {
  if (visible) {
    nextTick(() => {
      updateEditTooltipPosition()
      if (editTooltipRef.value) requestAnimationFrame(updateEditTooltipPosition)
    })
  } else {
    editTooltipPosition.value = null
  }
})

const openNewTabBtnRef = ref(null)
const openNewTabTooltipRef = ref(null)
const showOpenNewTabTooltip = ref(false)
const openNewTabTooltipPosition = ref(null)

const openNewTabTooltipStyle = computed(() => {
  if (!openNewTabTooltipPosition.value) return {}
  const { left, top } = openNewTabTooltipPosition.value
  return {
    left: `${left}px`,
    top: `${top}px`,
    transform: 'translateX(-50%)'
  }
})

function updateOpenNewTabTooltipPosition() {
  if (!openNewTabBtnRef.value || !openNewTabTooltipRef.value) return
  const rect = openNewTabBtnRef.value.getBoundingClientRect()
  const tip = openNewTabTooltipRef.value
  const tipWidth = tip.offsetWidth || 180
  const gap = 8
  const padding = 12
  const vw = window.innerWidth
  let left = rect.left + rect.width / 2
  const minLeft = padding + tipWidth / 2
  const maxLeft = vw - padding - tipWidth / 2
  left = Math.max(minLeft, Math.min(maxLeft, left))
  openNewTabTooltipPosition.value = {
    left,
    top: rect.bottom + gap
  }
}

watch(showOpenNewTabTooltip, (visible) => {
  if (visible) {
    nextTick(() => {
      updateOpenNewTabTooltipPosition()
      if (openNewTabTooltipRef.value) requestAnimationFrame(updateOpenNewTabTooltipPosition)
    })
  } else {
    openNewTabTooltipPosition.value = null
  }
})

const updateEditTipCalloutPosition = () => {
  const isVisible = showEditTipCallout.value || forceShowEditTip.value
  if (!isVisible || !editButtonRef.value || !editTipCalloutRef.value) return
  const rect = editButtonRef.value.getBoundingClientRect()
  const calloutRect = editTipCalloutRef.value.getBoundingClientRect()
  const gap = 8
  const padding = 16
  const arrowHeight = 8
  // Position callout to the left of edit button, vertically centered
  const left = Math.max(padding, rect.left - calloutRect.width - gap)
  let top = rect.top + rect.height / 2 - calloutRect.height / 2
  top = Math.max(padding, Math.min(window.innerHeight - calloutRect.height - padding, top))
  // Pointer center should align with edit button center
  const editBtnCenterY = rect.top + rect.height / 2
  const arrowTop = editBtnCenterY - top - arrowHeight / 2
  editTipCalloutStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    zIndex: 10010,
    '--edit-tip-arrow-top': `${Math.max(4, Math.min(calloutRect.height - arrowHeight - 4, arrowTop))}px`
  }
}
/** When opening add panel from a placeholder/empty section, this is set so the panel knows where to add. Null when opened from the Add menu. */
const addWidgetPanelTargetSectionId = ref(null)
/** Delay showing empty state to let Optix iframe settle (prevents slide-on-load) */
const emptyStateVisible = ref(false)
let emptyStateDelayTimer = null
/** Show skeleton loader only after 500ms of loading (avoids flash for fast loads) */
const showSkeletonWhileLoading = ref(false)
let loadingSkeletonTimer = null
// Trial mode: when enabled, widgets show fake data (successful flex space, 6 months)
// Seeded from ?trial= so side-panel mounts start in the right mode: their child
// components fetch on mount, before fetchOrgDetailsForTrialMode() below can resolve.
const trialMode = ref(getTrialModeFromURL())
const orgName = ref('')
const orgLogoUrl = ref('')
const { trialModeOverride } = provideDemoMode(trialMode)
const automationsFooterEnabled = provideAutomationsFooter(orgId)
provide('dashboardOrgId', orgId)
/** When has_signed_up is true, hide the trial toggle. When false, show it (and default trial on) */
const showTrialToggle = ref(true)

// Persist the operator's trial-mode toggle choice per org. Separate mounts
// (e.g. the side-panel iframe that renders WidgetDetailPanel) read this so they
// respect the toggle instead of re-defaulting trial mode on from billing.
const trialModePrefKey = () => `dashboard-trial-mode${orgId.value ? `-${orgId.value}` : ''}`
const getSavedTrialMode = () => {
  try {
    const v = localStorage.getItem(trialModePrefKey())
    return v === null ? null : v === 'true'
  } catch {
    return null
  }
}
const saveTrialModePref = (value) => {
  try {
    localStorage.setItem(trialModePrefKey(), String(value))
  } catch {
    // Ignore storage errors (e.g. private mode)
  }
}

const trialModeRefreshing = ref(false)
const onTrialModeChange = async () => {
  await nextTick() // Ensure v-model has propagated before we read trialMode
  const runRefresh = async () => {
    const modeToUse = trialMode.value
    saveTrialModePref(modeToUse)
    const dashboardContent = dashboardContentRef.value || document.querySelector('.dashboard-content')
    const scrollPosition = dashboardContent ? dashboardContent.scrollTop : (window.scrollY || document.documentElement.scrollTop)
    trialModeOverride.value = modeToUse
    dataFetcher.invalidateCache()
    trialModeRefreshing.value = true
    // Re-fetch best practices for the new mode
    fetchBestPractices({
      force: true,
      getDemoData: modeToUse ? getDemoBestPracticesData : null
    })
    try {
      await refreshAllWidgetData({ showLoading: true })
      showSnackbarNotify(modeToUse ? 'Showing demo data' : 'Showing real data')
    } finally {
      trialModeOverride.value = null
      trialModeRefreshing.value = false
      nextTick(() => {
        requestAnimationFrame(() => {
          const el = dashboardContentRef.value || document.querySelector('.dashboard-content')
          if (el) {
            el.scrollTop = scrollPosition
          } else {
            window.scrollTo(0, scrollPosition)
          }
          requestAnimationFrame(() => {
            const el2 = dashboardContentRef.value || document.querySelector('.dashboard-content')
            if (el2) el2.scrollTop = scrollPosition
            else window.scrollTo(0, scrollPosition)
          })
        })
      })
    }
  }
  if (trialModeRefreshing.value) {
    // Already refreshing - wait for it then refresh again with current trial mode
    while (trialModeRefreshing.value) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    runRefresh()
  } else {
    await runRefresh()
  }
}

// Mobile breakpoint (matches 768px used in CSS)
const isMobile = computed(() => windowWidth.value <= 768)

// Optix menu: show burger when viewport < 500px OR menu is closed (sidePanelOpen === false from ParentSize).
// Avoids showing the burger when menu is open on larger viewports.
const { showBurgerIcon, attachListener } = useOptixMenuState()
const showBurger = computed(() => windowWidth.value < 500 || showBurgerIcon.value)

// Detect if we're in an iframe (if not, we're in a new tab)
const isInIframe = computed(() => {
  try {
    return window.self !== window.top
  } catch (e) {
    // If we can't access window.top, we're likely in an iframe with cross-origin restrictions
    return true
  }
})

// Fetch current user for greeting
const { data: currentUserData } = useGraphQLData({
  dataKey: 'dashboardCurrentUser',
  query: `
    query GetCurrentUser {
      me {
        user {
          name
          surname
          user_id
        }
      }
    }
  `,
  variables: {},
  operationName: 'GetCurrentUser',
  priority: 10,
  enableBatching: true,
  autoFetch: true
})

// Fetch admin roles for widget visibility (matches current user by user_id)
const { data: adminRoleData } = useGraphQLData({
  dataKey: 'dashboardAdminRole',
  query: `
    query GetMyAdminRole {
      organization {
        admins {
          admin_role
          user {
            user_id
          }
        }
      }
    }
  `,
  variables: {},
  operationName: 'GetMyAdminRole',
  priority: 10,
  enableBatching: true,
  autoFetch: true
})

const { userRole, filterWidgetsByRole, canView } = useUserRole(currentUserData, adminRoleData)

// Org-level setting: when false, only owners (not managers) may see revenue & financial widgets.
// Default true (visible) to preserve existing behavior; loaded from storage via loadDashboard().
const managersCanViewRevenue = ref(true)

// Effective minimum role for a widget, accounting for the org-level revenue toggle.
// Revenue & financial widgets become OWNER-only when managers are not allowed to view them.
const effectiveMinRole = (def) => {
  if (def?.section === REVENUE_SECTION && !managersCanViewRevenue.value) return 'OWNER'
  return def?.minRole
}

const greetingTitle = computed(() => {
  const name = currentUserData.value?.me?.user?.name?.trim()
  return name ? `Hey there, ${name}` : 'Hey there'
})

const emptyStateGreeting = computed(() => {
  const name = currentUserData.value?.me?.user?.name?.trim()
  return name ? `Welcome, ${name}` : 'Welcome'
})

// Optix prompt input
const optixPromptText = ref('')
const optixPromptInput = ref(null)
const dashboardContentRef = ref(null)
const dashboardHeaderRef = ref(null)
const headerHeight = ref(0)
const showScrollChevron = ref(true)

// LLM mode: when on, shows Optix prompt, sections peeking, fade, chevron. When off, classic layout.
// Loaded from database, with localStorage fallback when DB unavailable
const llmEnabled = ref(
  (() => {
    try {
      return localStorage.getItem('dashboard-llm-enabled') === 'true'
    } catch {
      return false
    }
  })()
)

const handleOptixInput = () => {
  // Auto-resize textarea
  const el = optixPromptInput.value
  if (el) {
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }
}

const handleOptixKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleOptixSubmit()
  }
}

const handleOptixSubmit = () => {
  const text = optixPromptText.value?.trim()
  if (text) {
    // Placeholder for future Optix integration
    optixPromptText.value = ''
    const el = optixPromptInput.value
    if (el) el.style.height = 'auto'
  }
}

const handleOptixSuggestion = (type) => {
  const prompts = {
    insights: 'Summarize my dashboard performance and key trends',
    automations: 'What automations should I set up to save time?',
    bookings: 'What do I need to know about upcoming bookings?',
    tasks: "What's on my to-do list and what should I prioritize?",
    suggest: 'What should I focus on today?'
  }
  optixPromptText.value = prompts[type] || ''
  optixPromptInput.value?.focus()
}

// Get client_id and canvasUUID for OCC messages (same sources as useOptixPanel + window.name for canvas)
function getOptixParamsForOCC() {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const ref = new URL(document.referrer)
      if (!clientId) clientId = ref.searchParams.get('client_id') || ref.searchParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = ref.searchParams.get('canvasUUID') || ref.searchParams.get('canvas_uuid') || ref.searchParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  if (!canvasUUID && window.name && /^\d+-\d+$/.test(window.name)) {
    canvasUUID = window.name
  }
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentParams = new URLSearchParams(window.parent.location.search)
        if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || canvasUUID
      }
    } catch (_e) {}
  }
  return {
    client_id: clientId || undefined,
    canvasUUID: canvasUUID || `${Date.now()}-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`
  }
}

// Send a custom event to PostHog via the parent Optix app
function trackPosthogEvent(name, properties = {}) {
  if (!isInOptixIframe()) return
  const { canvasUUID } = getOptixParamsForOCC()
  window.parent.postMessage({
    command: 'PosthogTrackEvent',
    payload: { canvasUUID, name, properties }
  }, '*')
}
provide('trackPosthogEvent', trackPosthogEvent)

// Check if fullscreen mode is enabled via URL parameter
const isFullscreenMode = computed(() => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('fullscreen') === 'true'
})

// Effective LLM mode: ignore llmEnabled when in fullscreen (always use classic layout)
const effectiveLlmEnabled = computed(() => llmEnabled.value && !isFullscreenMode.value)

const urlRefreshTrigger = ref(0)

// Panel mode: loaded inside Optix parent's side panel (URL has ?panel=add-widget, ?panel=widget-settings, ?panel=widget-detail, or ?panel=inbox-conversation)
const isPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  const panel = urlParams.get('panel')
  return panel === 'add-widget' || panel === 'widget-settings' || panel === 'widget-detail' || panel === 'inbox-conversation' || panel === 'view-all-actions' || panel === 'view-all-time-saved' || panel === 'view-all-top-bookers' || panel === 'view-all-revenue-collected' || panel === 'view-all-support-chat' || panel === 'view-all-best-practices' || panel === 'dashboard-settings'
})

const isSettingsPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'dashboard-settings'
})

const isAddWidgetPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'add-widget'
})

const isWidgetDetailPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'widget-detail'
})

const widgetDetailPanelTitle = computed(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const title = urlParams.get('title')
  return title ? decodeURIComponent(title) : 'Widget'
})

const widgetDetailPanelType = computed(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const fromUrl = urlParams.get('widgetType')
  if (fromUrl) return fromUrl
  const title = widgetDetailPanelTitle.value
  return getWidgetTypeFromTitle(title)
})

const widgetDetailBestPracticeType = computed(() => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('bestPracticeType') || null
})

const isViewAllActionsPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-actions'
})

const isViewAllTimeSavedPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-time-saved'
})

const isViewAllTopBookersPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-top-bookers'
})

const isViewAllRevenueCollectedPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-revenue-collected'
})

const isViewAllConversionAccountsPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-conversion-accounts'
})

const isViewAllSupportChatPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-support-chat'
})

const isViewAllBestPracticesPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'view-all-best-practices'
})

const isDiscoverGroupsPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  const panel = urlParams.get('panel')
  return panel === 'discover-groups' || panel === 'view-all-groups'
})

// Inbox conversation panel (from Latest Messages widget)
const isInboxConversationPanelMode = computed(() => {
  urlRefreshTrigger.value
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('panel') === 'inbox-conversation' && urlParams.get('conversation')
})

const inboxConversationId = computed(() => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('conversation') || ''
})

const inboxConversationIframeSrc = computed(() => {
  if (!inboxConversationId.value) return ''
  const url = new URL(window.location.href)
  let baseOrigin = url.searchParams.get('optix_admin_origin') || url.searchParams.get('optixAdminOrigin')
  if (!baseOrigin && document.referrer) {
    try {
      const ref = new URL(document.referrer)
      if (ref.origin !== url.origin) baseOrigin = ref.origin
    } catch (_e) {}
  }
  if (!baseOrigin) baseOrigin = url.origin
  const panelUrl = new URL('/admin/inbox/conversation-panel', baseOrigin)
  const copyParams = ['token', 'organization_id', 'orgId', 'subdomain', 'locations', 'email', 'current_user_id', 'optix-placement', 'optix-mobile', '_ga']
  for (const key of copyParams) {
    const val = url.searchParams.get(key) || url.searchParams.get(key.replace(/_/g, '-'))
    if (val) panelUrl.searchParams.set(key, val)
  }
  panelUrl.searchParams.set('conversation', inboxConversationId.value)
  return panelUrl.toString()
})

const handleInboxConversationClose = () => {
  popAdminSidePanel()
}

// Widget settings panel params from URL (when in panel=widget-settings mode)
// Show edit controls when in iframe (but not in fullscreen mode) or when token is present (for direct browser access/testing, but not in fullscreen)
const showEditControls = computed(() => {
  // Hide edit controls when fullscreen=true (opened in new tab)
  if (isFullscreenMode.value) {
    return false
  }
  // Show when in iframe or when token is present (for testing)
  return isInIframe.value || !!token.value
})

// Dashboard storage composable
const { loadDashboard, saveDashboard, isSaving, error: dashboardError } = useDashboardStorage()

// Global snackbar composable
const { 
  showSnackbar, 
  snackbarMessage, 
  snackbarUndoAction, 
  snackbarWiggleKey,
  show: showSnackbarNotify,
  handleUndo: handleSnackbarUndo 
} = useSnackbar()

// Store the last saved version for cancel functionality
const lastSavedSections = ref(null)

const toggleEditMode = async (event) => {
  showEditTooltip.value = false
  if (event) {
    event.preventDefault()
    event.stopPropagation()
    // Prevent button from receiving focus
    event.target.blur()
  }
  
  // Save current scroll position from the scrollable container
  const dashboardContent = document.querySelector('.dashboard-content')
  const scrollPosition = dashboardContent ? dashboardContent.scrollTop : (window.scrollY || document.documentElement.scrollTop)
  
  // When entering edit mode, save current state as the "last saved" version
  if (!editMode.value) {
    trackPosthogEvent('Dashboard: Edit mode activated')
    // Deep clone the current sections to store as last saved version
    lastSavedSections.value = JSON.parse(JSON.stringify(sections.value))
  }
  
  editMode.value = !editMode.value
  
  // Restore scroll position multiple times to ensure it sticks
  await nextTick()
  requestAnimationFrame(() => {
    if (dashboardContent) {
      dashboardContent.scrollTop = scrollPosition
    } else {
      window.scrollTo(0, scrollPosition)
    }
    
    // Try again after another frame to catch any delayed layout changes
    requestAnimationFrame(() => {
      if (dashboardContent) {
        dashboardContent.scrollTop = scrollPosition
      } else {
        window.scrollTo(0, scrollPosition)
      }
    })
  })
  
  // Close add widget panel when exiting edit mode
  if (editMode.value === false) {
    showAddWidgetPanel.value = false
  }
}

const toggleLlmMode = () => {
  llmEnabled.value = !llmEnabled.value
  // Persists when user clicks Save (along with sections)
}

const saveEdit = async (event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
    event.target.blur()
  }
  
  // Commit any pending title changes by blurring all section title inputs
  // This ensures title changes are saved before we save the dashboard
  await nextTick()
  const titleInputs = document.querySelectorAll('.section-title-input')
  titleInputs.forEach(input => {
    if (document.activeElement === input) {
      input.blur()
    }
  })
  
  // Wait a tick for blur handlers to fire and update sections
  await nextTick()
  
  const saved = await saveDashboard(sections.value, llmEnabled.value)
  if (saved) {
    try {
      localStorage.setItem('dashboard-llm-enabled', String(llmEnabled.value))
    } catch {}
    // Update last saved version to current state
    lastSavedSections.value = JSON.parse(JSON.stringify(sections.value))
    // Exit edit mode
    editMode.value = false
    showAddWidgetPanel.value = false
  } else {
    console.warn('⚠️ Dashboard save failed')
  }
}

const cancelEdit = async (event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
    event.target.blur()
  }
  
  // Reload from database to restore the last saved version with components
  const result = await loadDashboard()
  // Set the org-level revenue setting before filtering, so filterSectionsForRole uses the correct value
  managersCanViewRevenue.value = result?.managers_can_view_revenue !== false
  const savedSections = result?.sections
  if (savedSections && savedSections.length > 0) {
    const filtered = filterSectionsForRole(savedSections)
    sections.value = filtered
    // Update last saved version to match what we just loaded
    lastSavedSections.value = JSON.parse(JSON.stringify(sections.value))
  } else {
    // If no saved dashboard exists, show empty state
    sections.value = []
    lastSavedSections.value = []
  }
  if (Array.isArray(result?.sections) && typeof result?.llm_enabled === 'boolean') {
    llmEnabled.value = result.llm_enabled
    try {
      localStorage.setItem('dashboard-llm-enabled', String(result.llm_enabled))
    } catch {}
  }

  // Exit edit mode
  editMode.value = false
  showAddWidgetPanel.value = false
}

const addSection = async () => {
  trackPosthogEvent('Dashboard: Section added')
  const sectionId = `section-${Date.now()}`
  const newSection = {
    id: sectionId,
    title: 'New Section',
    expanded: true,
    displayMode: 'detailed',
    widgets: []
  }
  sections.value = [newSection, ...sections.value]
  await nextTick()
  const dashboardContent = document.querySelector('.dashboard-content')
  if (dashboardContent) {
    dashboardContent.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const handleSectionUpdate = (sectionId, updates) => {
  if (updates.title) {
    trackPosthogEvent('Dashboard: Section renamed', { newTitle: updates.title })
  }
  const sectionIndex = sections.value.findIndex(s => s.id === sectionId)
  if (sectionIndex !== -1) {
    // Update the section by creating a new object to ensure Vue reactivity
    sections.value[sectionIndex] = {
      ...sections.value[sectionIndex],
      ...updates
    }
  }
}

const handleSectionDelete = (sectionId) => {
  sections.value = sections.value.filter(s => s.id !== sectionId)
}

const handleSectionMove = (sectionIndex, direction) => {
  if (direction === 'up' && sectionIndex > 0) {
    const newSections = [...sections.value]
    const temp = newSections[sectionIndex]
    newSections[sectionIndex] = newSections[sectionIndex - 1]
    newSections[sectionIndex - 1] = temp
    sections.value = newSections
  } else if (direction === 'down' && sectionIndex < sections.value.length - 1) {
    const newSections = [...sections.value]
    const temp = newSections[sectionIndex]
    newSections[sectionIndex] = newSections[sectionIndex + 1]
    newSections[sectionIndex + 1] = temp
    sections.value = newSections
  }
}

// Track recently removed widget IDs to prevent stale grid layout events from re-adding them
const recentlyRemovedWidgetIds = new Set()

const handleRemoveWidget = ({ sectionId, widgetId }) => {
  trackPosthogEvent('Dashboard: Widget removed', { widgetId, sectionId })
  recentlyRemovedWidgetIds.add(widgetId)
  const section = sections.value.find(s => s.id === sectionId)
  if (section) {
    section.widgets = section.widgets.filter(w => w.id !== widgetId)
  }
  // Clear after reactivity has fully propagated
  setTimeout(() => {
    recentlyRemovedWidgetIds.delete(widgetId)
  }, 500)
}


const handleWidgetsUpdate = (payload) => {
  const section = sections.value.find(s => s.id === payload.sectionId)
  if (section) {
    // Only track reorder event if widget positions/order actually changed (not just props)
    const oldOrder = section.widgets.map(w => `${w.id}:${w.x}:${w.y}:${w.w}:${w.h}`).join(',')
    const newOrder = payload.widgets.map(w => `${w.id}:${w.x}:${w.y}:${w.w}:${w.h}`).join(',')
    if (oldOrder !== newOrder) {
      trackPosthogEvent('Dashboard: Widget re-ordered', { sectionId: payload.sectionId })
    } else {
      // Positions unchanged — check if widget props changed (e.g. date range)
      const changedWidget = payload.widgets.find((w, i) => {
        const old = section.widgets[i]
        return old && JSON.stringify(old.props) !== JSON.stringify(w.props)
      })
      if (changedWidget) {
        trackPosthogEvent('Dashboard: Date range changed', {
          widgetId: changedWidget.id,
          widgetType: changedWidget.type,
          dateRange: changedWidget.props?.dateRange
        })
      }
    }
    // Filter out any widgets that were just removed — stale grid layout events can
    // race with removal and try to re-add them
    const widgets = recentlyRemovedWidgetIds.size > 0
      ? payload.widgets.filter(w => !recentlyRemovedWidgetIds.has(w.id))
      : payload.widgets
    section.widgets = widgets
  }
}

// Widget registry with metadata and categories (minRole = manager+ for revenue widgets)
const widgetRegistry = [
  {
    id: 'upcoming-bookings',
    name: 'Upcoming Bookings',
    category: 'Operations',
    section: 'bookings & assignments',
    description: 'View upcoming bookings and reservations. Shows a list of bookings with details like date, time, and user information.',
    component: UpcomingBookingsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'latest-messages',
    name: 'Inbox',
    category: 'Operations',
    section: 'operational',
    description: 'View your most recent conversations. Shows the 3 latest messages with avatar, sender name, date, and message preview.',
    component: LatestMessagesWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'upcoming-tours',
    name: 'Upcoming Tours',
    category: 'Operations',
    section: 'operations',
    description: 'Track scheduled tours and visits. Displays upcoming tours with tour details and contact information.',
    component: UpcomingToursWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'completed-tours',
    name: 'Completed Tours',
    category: 'Operations',
    section: 'operations',
    description: 'Track the number of tours that have been completed within the selected date range.',
    component: CompletedToursWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'plans-starting-soon',
    name: 'Plans Starting Soon',
    category: 'Operations',
    section: 'users & community',
    description: 'Monitor membership plans that are starting soon. Shows upcoming plan start dates and user details.',
    component: PlansStartingSoonWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'plans-ending-soon',
    name: 'Plans Ending Soon',
    category: 'Operations',
    section: 'users & community',
    description: 'Keep track of membership plans that are ending soon. Displays plans with end dates and user information.',
    component: PlansEndingSoonWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'public-group-conversations',
    name: 'Public group conversations',
    category: 'Operations',
    section: 'operational',
    description: 'Total number of public group conversations in your community. Shows the top groups by message count.',
    component: PublicGroupConversationsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'support-chat',
    name: 'Support chat',
    category: 'Operations',
    section: 'operational',
    description: 'Total number of support chat conversations between users and admins. Shows the top conversations by message count.',
    component: SupportChatWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'assignments-starting-soon',
    name: 'Assignments Starting Soon',
    category: 'Operations',
    section: 'bookings & assignments',
    description: 'Keep track of assignments that are starting soon. Displays assignments with start dates and user information.',
    component: AssignmentsStartingSoonWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'assignments-ending-soon',
    name: 'Assignments Ending Soon',
    category: 'Operations',
    section: 'bookings & assignments',
    description: 'Keep track of assignments that are ending soon. Displays assignments with end dates and user information.',
    component: AssignmentsEndingSoonWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'check-ins',
    name: 'Previous check-ins',
    category: 'Operations',
    section: 'operations',
    description: 'Monitor user check-ins. Shows recent check-in activity and statistics.',
    component: CheckInsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: { dateRange: 'last_7_days' },
    previewProps: {}
  },
  {
    id: 'checked-in',
    name: 'Currently checked in',
    category: 'Operations',
    section: 'operations',
    description: 'View who is currently checked in. Shows total count and a table with avatar, name, and location.',
    component: CheckedInWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'most-popular-automations',
    name: 'Best practices automated',
    category: 'Analytics',
    section: 'automations & efficiency',
    description: 'Top automations ranked by best practice coverage, proof count, and priority.',
    component: MostPopularAutomationsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    hasLargePreview: true,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'automation-enrolments',
    name: 'Most enrolled automations',
    category: 'Analytics',
    section: 'automations & efficiency',
    description: 'Automations ranked by enrolment count with a stacked bar visualization.',
    component: AutomationEnrolmentsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    hasLargePreview: true,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'recent-automation-enrollments',
    name: 'Recent Automation Enrollments',
    category: 'Other',
    section: 'automations & efficiency',
    description: 'See recent automation enrollments. Displays a list of recently enrolled users in automation workflows.',
    component: RecentAutomationEnrollmentsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: { rowSpan: 1, colSpan: 1 }
  },
  {
    id: 'custom-automations',
    name: 'Custom Automations',
    category: 'Other',
    section: 'automations & efficiency',
    description: 'View your custom automation workflows. Shows automation details and status.',
    component: CustomAutomationsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'actions-this-period',
    name: 'Tasks handled automatically',
    category: 'Analytics',
    section: 'automations & efficiency',
    description: 'View the number of actions completed in the current automation cycle. Shows the total count of actions executed.',
    component: ActionsThisPeriodWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'time-saved',
    name: 'Time Saved',
    category: 'Analytics',
    section: 'automations & efficiency',
    description: 'Track the total time saved through automation enrollments. Shows time saved metrics and breakdown by enrollment.',
    component: TimeSavedWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'invoices',
    name: 'Invoices',
    category: 'Analytics',
    section: 'revenue & financial',
    minRole: 'MANAGER',
    description: 'View the amount due for each invoice status (Overdue, Due, Upcoming). Shows a stacked bar and legend with currency-formatted amounts.',
    component: InvoicesWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    hasLargePreview: true,
    defaultProps: {
      dateRange: 'last_30_days'
    },
    previewProps: {}
  },
  {
    id: 'invoices-paid', // id kept for back-compat with saved dashboards
    name: 'Revenue collected',
    category: 'Analytics',
    section: 'revenue & financial',
    minRole: 'MANAGER',
    description: 'View the total payments received within a selected date range, based on when each payment was made. Excludes refunds. The detailed view breaks revenue down by account.',
    component: RevenueCollectedWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'unpaid-invoices',
    name: 'Unpaid invoices',
    category: 'Analytics',
    section: 'revenue & financial',
    minRole: 'MANAGER',
    description: 'View the total amount of unpaid invoices within a selected date range. Shows currency-formatted sum of unpaid invoice amounts.',
    component: UnpaidInvoicesWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'credit-owed',
    name: 'Credit owed',
    category: 'Analytics',
    section: 'revenue & financial',
    minRole: 'MANAGER',
    description: 'View the total credit owed from due or overdue invoices with negative balances.',
    component: CreditOwedWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: {},
    previewProps: {}
  },
  {
    id: 'plan-revenue',
    name: 'Plan revenue change',
    category: 'Analytics',
    section: 'revenue & financial',
    minRole: 'MANAGER',
    description: 'View the total revenue from plans within a selected date range. Shows a comparison with the previous period and a table of plans with the biggest change in revenue.',
    component: PlanRevenueWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {
      dateRange: 'last_30_days'
    },
    previewProps: { rowSpan: 1, colSpan: 1 },
    hiddenFromAddPanel: true
  },
  // Temporarily hidden - has issues causing errors
  // {
  //   id: 'top-accounts-by-revenue',
  //   name: 'Top accounts by revenue',
  //   category: 'Analytics',
  //   description: 'View top accounts by invoiced revenue for a selected period. Shows a table with account names and their total invoiced amounts.',
  //   component: TopAccountsByRevenueWidget,
  //   defaultColSpan: 2,
  //   defaultRowSpan: 2,
  //   hasDataTable: true,
  //   defaultProps: {
  //     dateRange: 'last_30_days',
  //     limit: 10
  //   },
  //   previewProps: {}
  // },
  {
    id: 'top-bookers',
    name: 'Top bookers',
    category: 'Analytics',
    section: 'bookings & assignments',
    description: 'View which users make the most bookings. Shows a ranked list of users by their booking count.',
    component: TopBookersWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {
      dateRange: 'last_30_days'
    },
    previewProps: {
      rowSpan: 1,
      colSpan: 1
    }
  },
  {
    id: 'tasks',
    name: 'Your tasks',
    category: 'Operations',
    section: 'operations',
    description: 'View your TODO tasks. Shows a list of tasks assigned to you with the ability to mark them as done.',
    component: TasksWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: true,
    defaultProps: {},
    previewProps: {
      rowSpan: 1,
      colSpan: 1
    }
  },
  {
    id: 'manual-vs-automated',
    name: 'Manual vs. automated actions',
    category: 'Analytics',
    section: 'automations & efficiency',
    description: 'Compare manual and automated actions. Shows a grouped bar chart displaying manual vs automated counts for different actions.',
    component: ManualVsAutomatedWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: {},
    previewProps: {},
    hiddenFromAddPanel: true
  },
  {
    id: 'converted-leads-from-inquiries',
    name: 'Inquiry → member conversions',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'View total converted leads from inquiries and a list of converted users with name and email.',
    component: ConvertedLeadsFromInquiriesWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'converted-leads-from-tours',
    name: 'Tour → member conversions',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'View total converted leads from tours and a list of converted users with name and email.',
    component: ConvertedLeadsFromToursWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'converted-leads-from-dropins',
    name: 'Drop-in → member conversions',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'View total converted leads from drop-ins and a list of converted users with name and email.',
    component: ConvertedLeadsFromDropinsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'conversion-rate-from-inquiries',
    name: 'Inquiry → member conversions rate',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'View the conversion rate percentage for inquiries.',
    component: ConversionRateFromInquiriesWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: {}
  },
  {
    id: 'conversion-rate-from-tours',
    name: 'Tour → member conversions rate',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'View the conversion rate percentage for tours.',
    component: ConversionRateFromToursWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: {}
  },
  {
    id: 'conversion-rate-from-dropins',
    name: 'Drop-in → member conversions rate',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'View the conversion rate percentage for drop-ins.',
    component: ConversionRateFromDropinsWidget,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    hasDataTable: false,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: {}
  },
  {
    id: 'tour-pass-plan-purchase',
    name: 'Tour → plan/pass purchase',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'Tour users that bought a pass or plan, with a list of converted users.',
    component: TourPassPlanPurchaseWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'dropin-repeat-booking',
    name: 'Drop-in → return booking',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'Drop-in users that made a second booking, with a list of converted users.',
    component: DropinRepeatBookingWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: { rowSpan: 2, colSpan: 1 }
  },
  {
    id: 'dropin-pass-plan-purchase',
    name: 'Drop-in → plan/pass purchase',
    category: 'Analytics',
    section: 'growth',
    minRole: 'MANAGER',
    description: 'Drop-in users that bought a pass or plan, with a list of converted users.',
    component: DropinPassPlanPurchaseWidget,
    defaultColSpan: 1,
    defaultRowSpan: 2,
    hasDataTable: true,
    defaultProps: { dateRange: 'last_30_days' },
    previewProps: { rowSpan: 2, colSpan: 1 }
  }
]

// Role-filtered widgets for Add panel (empty state) and sections - excludes role-restricted.
// Also applies the org-level revenue toggle (effectiveMinRole), so revenue widgets drop out for
// managers when owners have turned off manager access.
const availableWidgets = computed(() =>
  filterWidgetsByRole(widgetRegistry).filter(w => {
    const min = effectiveMinRole(w)
    return !min || canView(min)
  })
)

// All widgets for Add panel (includes role-restricted, shown blurred) - excludes hiddenFromAddPanel only
const allWidgetsForAddPanel = computed(() =>
  widgetRegistry.filter((w) => !w.hiddenFromAddPanel)
)

// Widget IDs the user cannot add due to role (shown blurred in Add panel)
const restrictedWidgetIds = computed(
  () =>
    new Set(
      widgetRegistry
        .filter((w) => {
          const min = effectiveMinRole(w)
          return min && !canView(min)
        })
        .map((w) => w.id)
    )
)

// Helper: get widget def from registry by id (handles timestamp suffixes)
const getWidgetDef = (widgetId) => {
  let def = widgetRegistry.find(w => w.id === widgetId)
  if (!def && widgetId?.includes('-')) {
    const baseFromSuffix = widgetId.replace(/-\d+(-\d+)*$/, '')
    def = widgetRegistry.find(w => w.id === baseFromSuffix)
  }
  if (!def && widgetId?.includes('-')) {
    const parts = widgetId.split('-')
    for (let i = parts.length - 1; i > 0; i--) {
      const testId = parts.slice(0, i).join('-')
      def = widgetRegistry.find(w => w.id === testId)
      if (def) break
    }
  }
  return def
}

// Filter section widgets by role (hide revenue etc. for users without access)
// If no role is available yet, skip filtering (show all widgets)
const filterSectionsForRole = (sectionsToFilter) => {
  if (!sectionsToFilter || !Array.isArray(sectionsToFilter)) return sectionsToFilter
  if (!userRole.value) return sectionsToFilter
  return sectionsToFilter
    .map(section => ({
      ...section,
      widgets: (section.widgets || []).filter(w => {
        const def = getWidgetDef(w.id)
        const min = effectiveMinRole(def)
        return !min || canView(min)
      })
    }))
    .filter(section => (section.widgets?.length ?? 0) > 0)
}

// Get all existing widgets across all sections (with section info for tooltips)
const allExistingWidgets = computed(() => {
  return sections.value.flatMap(section =>
    (section.widgets || []).map(w => ({
      ...w,
      sectionId: section.id,
      sectionTitle: section.title
    }))
  )
})

// Check if dashboard has any widgets
const hasWidgets = computed(() => {
  return sections.value.some(section => section.widgets && section.widgets.length > 0)
})

// Check if empty state is showing
const isShowingEmptyState = computed(() => {
  return !initialLoading.value && (!sections.value || sections.value.length === 0 || (!hasWidgets.value && !editMode.value))
})

// Show skeleton after 500ms of loading; only show "what's important" once we know it's empty
watch(initialLoading, (loading) => {
  if (loadingSkeletonTimer) {
    clearTimeout(loadingSkeletonTimer)
    loadingSkeletonTimer = null
  }
  if (loading) {
    loadingSkeletonTimer = setTimeout(() => {
      showSkeletonWhileLoading.value = true
      loadingSkeletonTimer = null
    }, 500)
  } else {
    showSkeletonWhileLoading.value = false
  }
}, { immediate: true })

// Delay empty state content visibility to let Optix iframe settle (prevents slide-on-load)
watch(isShowingEmptyState, (showing) => {
  if (emptyStateDelayTimer) {
    clearTimeout(emptyStateDelayTimer)
    emptyStateDelayTimer = null
  }
  if (showing) {
    emptyStateVisible.value = false
    emptyStateDelayTimer = setTimeout(() => {
      emptyStateVisible.value = true
      emptyStateDelayTimer = null
    }, 180)
  } else {
    emptyStateVisible.value = false
  }
}, { immediate: true })

const openAddWidgetPanel = async (targetSectionId = null) => {
  addWidgetPanelTargetSectionId.value = targetSectionId ?? null
  if (isInOptixIframe() && !isPanelMode.value) {
    // Save sections before opening the panel iframe so it loads the latest state from storage
    await saveDashboard(sections.value, llmEnabled.value)
    const panelParams = addWidgetPanelTargetSectionId.value
      ? { sectionId: addWidgetPanelTargetSectionId.value }
      : {}
    pushAdminSidePanel('add-widget', panelParams)
  } else {
    showAddWidgetPanel.value = true
  }
}

const handlePanelClose = () => {
  if (isPanelMode.value) {
    popAdminSidePanel()
  } else {
    showAddWidgetPanel.value = false
  }
}

const openSettingsPanel = () => {
  if (isInOptixIframe() && !isPanelMode.value) {
    pushAdminSidePanel('dashboard-settings')
  } else {
    showSettingsPanel.value = true
  }
}

const handleSettingsPanelClose = () => {
  if (isSettingsPanelMode.value) {
    popAdminSidePanel()
  } else {
    showSettingsPanel.value = false
  }
}

const handleWidgetDetailPanelClose = () => {
  if (isWidgetDetailPanelMode.value) {
    const closed = popAdminSidePanel()
    // Same-page fallback: remove panel params so grid shows (icon will read localStorage and show grey)
    if (!closed && window.parent === window) {
      const url = new URL(window.location.href)
      url.searchParams.delete('panel')
      url.searchParams.delete('title')
      url.searchParams.delete('widgetType')
      window.history.replaceState({}, '', url.toString())
      urlRefreshTrigger.value++
    }
  }
}

const handleViewAllPanelClose = () => {
  const closed = popAdminSidePanel()
  if (!closed && window.parent === window) {
    const url = new URL(window.location.href)
    url.searchParams.delete('panel')
    window.history.replaceState({}, '', url.toString())
    urlRefreshTrigger.value++
  }
}


// Template dashboard configuration
const templateDashboard = [
  {
    "id": "section-1770922128807",
    "title": "Your focus",
    "widgets": [
      {
        "id": "checked-in-1770962620711",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "upcoming-tours-1770923133956",
        "props": {
          "dateRange": "next_7_days"
        },
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "tasks-1770923204322",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      }
    ],
    "expanded": true,
    "position": 0,
    "displayMode": "focused"
  },
  {
    "id": "section-1770242943789",
    "title": "Your automated operations",
    "widgets": [
      {
        "id": "most-popular-automations-1770325553829-1770325553829",
        "props": {
          "dateRange": "last_30_days"
        },
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "actions-this-period-1770250703584-1770250703585",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "time-saved-1770268592501-1770268592501",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "recent-automation-enrollments-1770250677796-1770250677796",
        "props": {
          "dateRange": "today"
        },
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "custom-automations-1770775387865",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      }
    ],
    "expanded": true,
    "position": 1,
    "displayMode": "detailed"
  },
  {
    "id": "section-1771003825063",
    "title": "Growth",
    "widgets": [
      {
        "id": "converted-leads-from-inquiries-1771003834122",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "converted-leads-from-tours-1771003832593",
        "props": {
          "dateRange": "last_30_days"
        },
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "converted-leads-from-dropins-1771003835772",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "conversion-rate-from-inquiries-1771003835100",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "conversion-rate-from-tours-1771003833298",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "conversion-rate-from-dropins-1771003836493",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      }
    ],
    "expanded": true,
    "position": 2,
    "displayMode": "focused"
  },
  {
    "id": "section-1770324514990",
    "title": "Resources",
    "widgets": [
      {
        "id": "upcoming-bookings-1770324522967-1770324522967",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "assignments-starting-soon-1771447803029",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "assignments-ending-soon-1771447803987",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      }
    ],
    "expanded": true,
    "position": 3,
    "displayMode": "detailed"
  },
  {
    "id": "section-1770415759122",
    "title": "Users & community",
    "widgets": [
      {
        "id": "plans-starting-soon-1770415803316-1770415803316",
        "props": {},
        "colSpan": 1,
        "rowSpan": 2
      },
      {
        "id": "plans-ending-soon-1770415804889-1770415804890",
        "props": {},
        "colSpan": 1,
        "rowSpan": 2
      }
    ],
    "expanded": true,
    "position": 4,
    "displayMode": "detailed"
  },
  {
    "id": "default-section",
    "title": "Revenue",
    "widgets": [
      {
        "id": "invoices-1770686370437-1770686370437",
        "props": {
          "dateRange": "last_90_days"
        },
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "invoices-paid-1770319577003-1770319577003",
        "props": {
          "dateRange": "this_month"
        },
        "colSpan": 1,
        "rowSpan": 1
      },
      {
        "id": "unpaid-invoices-1770416814775-1770416814775",
        "props": {},
        "colSpan": 1,
        "rowSpan": 1
      }
    ],
    "expanded": true,
    "position": 5,
    "displayMode": "detailed"
  }
]

// Convert template JSON to sections with Vue components (filters by role)
const convertTemplateToSections = (template) => {
  return template.map(section => ({
    id: section.id,
    title: section.title,
    expanded: section.expanded !== undefined ? section.expanded : true,
    displayMode: section.displayMode || 'detailed',
    widgets: (section.widgets || []).map(widget => {
      const widgetDef = getWidgetDef(widget.id)
      if (!widgetDef) {
        console.warn(`Widget not found for ID: ${widget.id}`)
        return null
      }
      const min = effectiveMinRole(widgetDef)
      if (min && !canView(min)) {
        return null // Hide widget user cannot access
      }
      return {
        id: widget.id,
        component: markRaw(widgetDef.component),
        colSpan: widget.colSpan || 1,
        rowSpan: widget.rowSpan || 1,
        x: widget.x !== undefined ? widget.x : undefined,
        y: widget.y !== undefined ? widget.y : undefined,
        props: widget.props || {}
      }
    }).filter(Boolean)
  }))
}

// Category order for empty state (matches AddWidgetPanel); automations always last
const DASHBOARD_SECTION_ORDER = [
  'operational',
  'bookings & assignments',
  'operations',
  'revenue & financial',
  'growth',
  'users & community',
  'automations & efficiency'
]

// Role-based default sections (non-trial users get these automatically)
// Order mirrors trial: Revenue → Growth → Automations → Bookings, with Inbox/Operations added
const ROLE_DEFAULT_SECTIONS = {
  OWNER: ['revenue & financial', 'growth', 'automations & efficiency', 'bookings & assignments', 'operational', 'operations', 'users & community'],
  MANAGER: ['revenue & financial', 'growth', 'automations & efficiency', 'bookings & assignments', 'operational', 'operations', 'users & community'],
  CLIENT_SERVICES: ['automations & efficiency', 'operational', 'bookings & assignments', 'operations', 'users & community'],
  RECEPTIONIST: ['automations & efficiency', 'operational', 'bookings & assignments', 'operations']
}

function sectionDisplayName(sectionKey) {
  if (!sectionKey || typeof sectionKey !== 'string') return sectionKey
  if (sectionKey === 'operational') return 'Inbox'
  if (sectionKey === 'operations') return 'Operations'
  if (sectionKey === 'bookings & assignments') return 'Bookings & assignments'
  if (sectionKey === 'users & community') return 'Plans'
  return sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1).toLowerCase()
}

// Categories for empty state with widgets (excludes hiddenFromAddPanel, excludes automations - always added last)
const AUTOMATIONS_SECTION = 'automations & efficiency'

const REVENUE_SECTION = 'revenue & financial'
const GROWTH_SECTION = 'growth'

const dashboardCategoryGroups = computed(() => {
  const groups = new Map()
  for (const section of DASHBOARD_SECTION_ORDER) {
    groups.set(section, [])
  }
  for (const widget of availableWidgets.value) {
    if (widget.hiddenFromAddPanel) continue
    const section = widget.section || 'operational'
    if (!groups.has(section)) groups.set(section, [])
    groups.get(section).push(widget)
  }
  return DASHBOARD_SECTION_ORDER
    .filter(section => section !== AUTOMATIONS_SECTION && groups.get(section)?.length)
    .filter(section => {
      // Revenue & financial respects the org-level toggle (OWNER-only when managers are excluded)
      if (section === REVENUE_SECTION) return canView(managersCanViewRevenue.value ? 'MANAGER' : 'OWNER')
      if (section === GROWTH_SECTION) return canView('MANAGER')
      return true
    })
    .map(section => ({
      section,
      displayName: sectionDisplayName(section),
      widgets: groups.get(section)
    }))
})

// Widget IDs that go in Your Focus section (excluded from category sections)
const FOCUS_WIDGET_IDS = ['checked-in', 'upcoming-tours', 'time-saved']

const selectedCategories = ref([])
const setupCategoryListRef = ref(null)
let setupCategorySortable = null

const categoryDisplayOrder = ref([])

watch(dashboardCategoryGroups, (groups) => {
  const current = categoryDisplayOrder.value
  const newSections = new Set(groups.map(g => g.section))
  // Keep existing order for sections still present, then append any new ones
  const kept = current.filter(s => newSections.has(s))
  const added = groups.map(g => g.section).filter(s => !kept.includes(s))
  categoryDisplayOrder.value = [...kept, ...added]
}, { immediate: true })

const orderedCategoryGroups = computed(() => {
  const groups = dashboardCategoryGroups.value
  const groupMap = Object.fromEntries(groups.map(g => [g.section, g]))
  return categoryDisplayOrder.value
    .map(section => groupMap[section])
    .filter(Boolean)
})

const toggleCategory = (sectionKey) => {
  const idx = selectedCategories.value.indexOf(sectionKey)
  if (idx >= 0) {
    selectedCategories.value = selectedCategories.value.filter(s => s !== sectionKey)
  } else {
    selectedCategories.value = [...selectedCategories.value, sectionKey]
  }
}

watch(setupCategoryListRef, (el) => {
  if (el) nextTick(() => initSetupCategorySortable())
}, { immediate: true })

const initSetupCategorySortable = () => {
  if (setupCategorySortable) {
    setupCategorySortable.destroy()
    setupCategorySortable = null
  }
  if (!setupCategoryListRef.value) return
  setupCategorySortable = new Sortable(setupCategoryListRef.value, {
    animation: 200,
    handle: '.category-btn-drag-handle',
    ghostClass: 'empty-state-category-item--ghost',
    dragClass: 'empty-state-category-item--drag',
    onEnd: () => {
      const items = setupCategoryListRef.value.children
      const newOrder = []
      for (const item of items) {
        const section = item.dataset.section
        if (section) newOrder.push(section)
      }
      categoryDisplayOrder.value = newOrder
    }
  })
}

const createDashboardFromCategories = async ({ roleSections } = {}) => {
  const sectionsToCreate = []

  // Always add Your Focus section at top with displayMode: focused
  const focusWidgetDefs = FOCUS_WIDGET_IDS
    .map(id => availableWidgets.value.find(w => w.id === id))
    .filter(Boolean)
  const ts = Date.now()
  const focusWidgets = focusWidgetDefs.map((w, i) => ({
    id: `${w.id}-${ts}-${i}`,
    component: markRaw(w.component),
    colSpan: w.defaultColSpan ?? 1,
    rowSpan: w.defaultRowSpan ?? 1,
    props: w.defaultProps || {}
  }))
  sectionsToCreate.push({
    id: `section-${Date.now()}-focus`,
    title: 'Your Focus',
    expanded: true,
    displayMode: 'focused',
    widgets: focusWidgets
  })

  // Trial mode: fixed section order, excluding Operations and Inbox
  // Role-based: use roleSections parameter
  // Manual: user-chosen display order from category picker
  const TRIAL_SECTION_ORDER = ['revenue & financial', 'growth', 'automations & efficiency', 'bookings & assignments', 'users & community']
  const TRIAL_EXCLUDED_SECTIONS = ['operational', 'operations']

  // Build a map of all available sections (including automations which is normally separate)
  const allGroupsBySection = Object.fromEntries(dashboardCategoryGroups.value.map(g => [g.section, g]))
  // Also add automations section to the map since it's excluded from dashboardCategoryGroups
  const automationsWidgetList = availableWidgets.value.filter(w => !w.hiddenFromAddPanel && w.section === AUTOMATIONS_SECTION)
  if (automationsWidgetList.length) {
    allGroupsBySection[AUTOMATIONS_SECTION] = {
      section: AUTOMATIONS_SECTION,
      displayName: sectionDisplayName(AUTOMATIONS_SECTION),
      widgets: automationsWidgetList
    }
  }

  let sectionOrder
  if (trialMode.value) {
    sectionOrder = TRIAL_SECTION_ORDER
  } else if (roleSections) {
    sectionOrder = roleSections
  } else {
    sectionOrder = [...categoryDisplayOrder.value.filter(s => selectedCategories.value.includes(s)), AUTOMATIONS_SECTION]
  }

  for (const sectionKey of sectionOrder) {
    if (trialMode.value && TRIAL_EXCLUDED_SECTIONS.includes(sectionKey)) continue
    const group = allGroupsBySection[sectionKey]
    if (!group) continue
    // For manual picker mode, skip automations if not selected (it's always appended)
    if (!trialMode.value && !roleSections && sectionKey !== AUTOMATIONS_SECTION && !selectedCategories.value.includes(sectionKey)) continue
    const catTs = Date.now()
    const categoryWidgets = group.widgets
      .filter(w => !FOCUS_WIDGET_IDS.includes(w.id))
      .map((w, i) => ({
        id: `${w.id}-${catTs}-${i}`,
        component: markRaw(w.component),
        colSpan: w.defaultColSpan ?? 1,
        rowSpan: w.defaultRowSpan ?? 1,
        props: w.defaultProps || {}
      }))
    if (!categoryWidgets.length) continue
    sectionsToCreate.push({
      id: `section-${Date.now()}-${group.section.replace(/\s+/g, '-')}`,
      title: group.displayName,
      expanded: true,
      displayMode: (sectionKey === 'bookings & assignments' || sectionKey === 'users & community' || sectionKey === 'automations & efficiency') ? 'detailed' : 'focused',
      widgets: categoryWidgets
    })
  }

  sections.value = sectionsToCreate
  lastSavedSections.value = JSON.parse(JSON.stringify(sections.value))
  selectedCategories.value = []
  categoryDisplayOrder.value = []
  if (setupCategorySortable) {
    setupCategorySortable.destroy()
    setupCategorySortable = null
  }
  await saveDashboard(sections.value, llmEnabled.value)
  // Show edit tip callout after new dashboard loads
  await nextTick()
  showEditTipCallout.value = true
}

const handleWidgetSettingsOpen = () => {
  // Close the add widget panel when settings are opened (widgets open their own settings panel)
  if (showAddWidgetPanel.value) {
    showAddWidgetPanel.value = false
  }
}

const handleAddWidget = async ({ sectionId, widget, openPanel }) => {
  // If openPanel is true, just open the panel (pass sectionId so panel knows target section)
  if (openPanel) {
    openAddWidgetPanel(sectionId ?? null)
    return
  }

  if (!widget) return
  trackPosthogEvent('Dashboard: Widget added', { widgetId: widget.id, sectionId })
  const section = sections.value.find(s => s.id === sectionId)
  if (section) {
    // Check if widget already exists in this section
    const existingWidget = section.widgets.find(w => w.id === widget.id)
    if (existingWidget) {
      console.warn(`Widget ${widget.id} already exists in section ${sectionId}`)
      return
    }
    
    // Create a unique widget instance with timestamp suffix to allow multiple instances
    const uniqueWidget = {
      ...widget,
      id: `${widget.id}-${Date.now()}`
    }
    // Remove x,y from widget - we store order only; position is computed from index
    delete uniqueWidget.x
    delete uniqueWidget.y

    // Insert at position when x,y provided (e.g. drop on empty slot), otherwise append
    const currentWidgets = [...(section.widgets || [])]
    if (widget.x !== undefined && widget.y !== undefined && columns.value != null) {
      const insertIndex = Math.min(
        widget.y * columns.value + widget.x,
        currentWidgets.length
      )
      currentWidgets.splice(insertIndex, 0, uniqueWidget)
      section.widgets = currentWidgets
    } else {
      section.widgets = [...currentWidgets, uniqueWidget]
    }
    
    // In panel mode: save to storage and broadcast to main dashboard
    if (isPanelMode.value) {
      await saveDashboard(sections.value, llmEnabled.value)
      try {
        const channel = new BroadcastChannel(DASHBOARD_BROADCAST_CHANNEL)
        const baseWidgetId = uniqueWidget.id.replace(/-\d+$/, '')
        channel.postMessage({
          type: 'DASHBOARD_ADD_WIDGET',
          sectionId,
          widget: {
            id: uniqueWidget.id,
            baseWidgetId,
            defaultColSpan: uniqueWidget.defaultColSpan,
            defaultRowSpan: uniqueWidget.defaultRowSpan,
            name: uniqueWidget.name,
            category: uniqueWidget.category
          }
        })
        channel.close()
      } catch (_e) {}
    }
  }
}

// Calculate responsive columns based on available width
// < 650px: 1 column. 650–1100px: 2 columns. >= 1100px: up to 3 columns.
const columns = computed(() => {
  const width = windowWidth.value
  if (width < 650) return 1
  if (width < 1100) return 2
  const minWidgetWidth = 250
  const gap = 8
  const dashboardPadding = 40 // 20px on each side
  const availableWidth = width - dashboardPadding
  const calculatedColumns = Math.floor((availableWidth + gap) / (minWidgetWidth + gap))
  return Math.max(1, Math.min(3, calculatedColumns))
})

// Row height: 312px base. Sections add +48px when they contain widgets that show the automations footer.
const rowHeight = 312

const handleResize = () => {
  windowWidth.value = window.innerWidth
  viewportHeight.value = window.innerHeight
  if (dashboardHeaderRef.value) {
    headerHeight.value = dashboardHeaderRef.value.offsetHeight
  }
}

// Silent refresh interval (60 seconds)

const openInNewTab = () => {
  if (!token.value) {
    console.warn('openInNewTab: No token available')
    return
  }
  
  // Store token securely in localStorage with a session ID (as backup)
  const sessionId = storeTokenForSession(token.value)
  
  // Construct URL with session ID (not token) and fullscreen parameters
  const baseUrl = window.location.origin + window.location.pathname
  const url = `${baseUrl}?sessionId=${encodeURIComponent(sessionId)}&fullscreen=true`
  
  
  // Open in new tab
  const newWindow = window.open(url, '_blank')
  
  // Send token via postMessage as primary method (more reliable than localStorage)
  if (newWindow) {
    // Wait for the new window to load, then send the token
    const sendToken = () => {
      try {
        newWindow.postMessage({
          type: 'DASHBOARD_TOKEN',
          token: token.value,
          sessionId: sessionId
        }, window.location.origin)
      } catch (error) {
        console.error('Failed to send token via postMessage:', error)
      }
    }
    
    // Try sending immediately
    sendToken()
    
    // Also try after a short delay in case the window isn't ready
    setTimeout(sendToken, 100)
    setTimeout(sendToken, 500)
  }
}

// Default sections configuration - used as fallback if no saved dashboard exists
const getDefaultSections = () => [
  {
    id: 'default-section',
    title: 'Main Dashboard',
    expanded: true,
    widgets: [
      {
        id: 'time-saved',
        component: markRaw(TimeSavedWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'upcoming-bookings',
        component: markRaw(UpcomingBookingsWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'upcoming-tours',
        component: markRaw(UpcomingToursWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'completed-tours',
        component: markRaw(CompletedToursWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'plans-starting-soon',
        component: markRaw(PlansStartingSoonWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'assignments-ending-soon',
        component: markRaw(AssignmentsEndingSoonWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'custom-automations',
        component: markRaw(CustomAutomationsWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'recent-automation-enrollments',
        component: markRaw(RecentAutomationEnrollmentsWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      },
      {
        id: 'check-ins',
        component: markRaw(CheckInsWidget),
        colSpan: 1,
        rowSpan: 1,
        props: {}
      }
    ]
  }
]

// Sections configuration - each section has its own grid
// Start with empty array to show empty state if no saved dashboard exists
const sections = ref([])

// Spacer height = viewport - peek - header height (sections positioned at bottom)
const sectionsSpacerStyle = computed(() => {
  const peek = 180 // more visible to compensate for title moved down
  const height = Math.max(0, viewportHeight.value - peek - headerHeight.value)
  return { minHeight: `${height}px` }
})

// Scroll chevron: attach listener when dashboard content mounts
watch(
  dashboardContentRef,
  (el) => {
    if (!el || !window._dashboardScrollChevronHandler) return
    el.addEventListener('scroll', window._dashboardScrollChevronHandler)
    showScrollChevron.value = el.scrollTop < 30
  },
  { immediate: true }
)

// Update header height when header mounts or resizes
let headerResizeObserver = null
watch(
  dashboardHeaderRef,
  (el) => {
    if (headerResizeObserver) {
      headerResizeObserver.disconnect()
      headerResizeObserver = null
    }
    if (el) {
      headerHeight.value = el.offsetHeight
      headerResizeObserver = new ResizeObserver(() => {
        headerHeight.value = el.offsetHeight
      })
      headerResizeObserver.observe(el)
    } else {
      headerHeight.value = 0
    }
  },
  { immediate: true }
)

// Update edit tip callout position when it becomes visible or on resize/scroll
let editTipScrollResizeCleanup = null
watch([showEditTipCallout, forceShowEditTip], ([show, force]) => {
  const visible = show || force
  if (!visible) {
    editTipCalloutStyle.value = null
    if (editTipScrollResizeCleanup) {
      editTipScrollResizeCleanup()
      editTipScrollResizeCleanup = null
    }
    return
  }
  nextTick(() => {
    requestAnimationFrame(() => {
      updateEditTipCalloutPosition()
      const onUpdate = () => updateEditTipCalloutPosition()
      window.addEventListener('resize', onUpdate)
      window.addEventListener('scroll', onUpdate, true)
      editTipScrollResizeCleanup = () => {
        window.removeEventListener('resize', onUpdate)
        window.removeEventListener('scroll', onUpdate, true)
      }
    })
  })
}, { immediate: true })

// Watch editMode changes and maintain scroll position
let savedScrollPosition = null
watch(() => editMode.value, () => {
  const dashboardContent = document.querySelector('.dashboard-content')
  if (dashboardContent) {
    // Save scroll position before change
    savedScrollPosition = dashboardContent.scrollTop
    
    // Restore after DOM updates
    nextTick(() => {
      requestAnimationFrame(() => {
        if (dashboardContent && savedScrollPosition !== null) {
          dashboardContent.scrollTop = savedScrollPosition
        }
      })
    })
  }
})

onMounted(async () => {
  try {
    // For testing: clear insight dismissed state on page load so suggestion comes back on refresh
    clearInsightDismissedOnLoad()

    // Validate iframe parameters - token is optional for development
    const validation = validateIframeParams()
    
    // Set orgId and token if available, but don't block dashboard from loading
    orgId.value = validation.orgId
    token.value = validation.token || getTokenFromURL()
    
    // Check if we're in fullscreen mode and waiting for token
    const urlParams = new URLSearchParams(window.location.search)
    const isFullscreen = urlParams.get('fullscreen') === 'true'
    const waitingForToken = isFullscreen && !token.value
    
    // Set up window resize listener for responsive columns
    window.addEventListener('resize', handleResize)

    // Listen for ParentSize broadcasts (sidePanelOpen) to show/hide burger
    attachListener()
    requestParentSize()

    // PostHog: Dashboard viewed (skip in panel mode - side panels remount the dashboard)
    if (!isPanelMode.value) {
      trackPosthogEvent('Dashboard: Viewed')
    }

    // Scroll chevron: hide when user scrolls down
    const handleScrollChevron = () => {
      const el = dashboardContentRef.value
      if (el) {
        showScrollChevron.value = el.scrollTop < 30
      }
    }
    window._dashboardScrollChevronHandler = handleScrollChevron

    // Listen for add-widget from panel mode (BroadcastChannel - same-origin)
    const broadcastChannel = new BroadcastChannel(DASHBOARD_BROADCAST_CHANNEL)
    const handleBroadcastMessage = (event) => {
      if (event.data?.type === 'DASHBOARD_ADD_WIDGET' && !isPanelMode.value) {
        const { sectionId, widget: broadcastWidget } = event.data
        const section = sections.value.find(s => s.id === sectionId)
        if (!section) return
        const fullWidget = availableWidgets.value.find(w => w.id === broadcastWidget.baseWidgetId)
        if (!fullWidget) return
        // Check if widget already exists (by the broadcast's unique ID)
        if ((section.widgets || []).some(w => w.id === broadcastWidget.id)) return
        // Add directly using the broadcast's unique ID — don't go through handleAddWidget
        // which would append another timestamp, causing ID mismatch with the database
        section.widgets = [...(section.widgets || []), {
          id: broadcastWidget.id,
          component: markRaw(fullWidget.component),
          colSpan: broadcastWidget.defaultColSpan || 1,
          rowSpan: broadcastWidget.defaultRowSpan || 1,
          props: fullWidget.defaultProps || {}
        }]
      }
      if (event.data?.type === 'DASHBOARD_WIDGET_SETTINGS_UPDATE' && !isPanelMode.value) {
        const { widgetId, sectionId, settings } = event.data
        const section = sections.value.find(s => s.id === sectionId)
        if (section) {
          const widgetIndex = (section.widgets || []).findIndex(w => w.id === widgetId)
          if (widgetIndex !== -1) {
            const updated = [...(section.widgets || [])]
            updated[widgetIndex] = {
              ...updated[widgetIndex],
              props: { ...(updated[widgetIndex].props || {}), ...settings }
            }
            section.widgets = updated
          }
        }
      }
      if (event.data?.type === 'DASHBOARD_REMOVE_WIDGET' && !isPanelMode.value) {
        const { widgetId, sectionId } = event.data
        handleRemoveWidget({ sectionId, widgetId })
      }
    }
    broadcastChannel.addEventListener('message', handleBroadcastMessage)
    window._dashboardBroadcastChannel = broadcastChannel
    window._dashboardBroadcastHandler = handleBroadcastMessage

    // Fetch org billing to set trial mode default and show/hide toggle
    const fetchOrgDetailsForTrialMode = async () => {
      // URL param ?trial=true or ?trial_mode=true enables trial mode (overrides org billing)
      if (getTrialModeFromURL()) {
        showTrialToggle.value = true
        trialMode.value = true
        return
      }
      try {
        const result = await getOrganizationDetails()
        const org = result?.data?.organization || result?.organization
        orgName.value = org?.name || ''
        orgLogoUrl.value = org?.square_logo?.url || ''
        const hasSignedUp = org?.optix_billing?.has_signed_up
        if (hasSignedUp === true) {
          showTrialToggle.value = false
        } else if (hasSignedUp === false) {
          showTrialToggle.value = true
          // Default trial on, but respect the operator's saved toggle choice
          // (so a trial org that turned trial mode off gets working links here too).
          const saved = getSavedTrialMode()
          trialMode.value = saved != null ? saved : true
        }
      } catch (err) {
        console.warn('Could not fetch org billing for trial mode:', err)
      }
    }

    // Function to load dashboard data
    const loadDashboardData = async () => {
      try {
        await fetchOrgDetailsForTrialMode()
        const result = await loadDashboard()
        // Set the org-level revenue setting before filtering/creating sections, so role-based filtering uses the correct value
        managersCanViewRevenue.value = result?.managers_can_view_revenue !== false
        const savedSections = result?.sections
        if (savedSections && savedSections.length > 0) {
          const filtered = filterSectionsForRole(savedSections)
          sections.value = filtered
          // Store as last saved version
          lastSavedSections.value = JSON.parse(JSON.stringify(sections.value))
        } else if (trialMode.value) {
          // Trial mode with no saved dashboard - auto-create with trial section order
          await createDashboardFromCategories()
        } else {
          // No saved dashboard - auto-create based on user role
          // Check for URL role override (for testing: ?role=OWNER, ?role=RECEPTIONIST, etc.)
          const urlRoleOverride = new URLSearchParams(window.location.search).get('role')?.toUpperCase()
          const validRoleOverride = urlRoleOverride && ROLE_DEFAULT_SECTIONS[urlRoleOverride] ? urlRoleOverride : null

          // Wait for role to be available (max 5s) since it loads async
          let role = validRoleOverride || userRole.value
          if (!role) {
            role = await new Promise((resolve) => {
              const timeout = setTimeout(() => {
                stopWatch()
                resolve(null)
              }, 5000)
              const stopWatch = watch(userRole, (newRole) => {
                if (newRole) {
                  clearTimeout(timeout)
                  stopWatch()
                  resolve(newRole)
                }
              }, { immediate: true })
            })
          }
          if (validRoleOverride) {
            console.log(`[Dashboard] Using URL role override: ${validRoleOverride}`)
          }
          console.log(`[Dashboard] Creating default dashboard for role: ${role || 'unknown (falling back to RECEPTIONIST)'}`)
          const defaultSections = ROLE_DEFAULT_SECTIONS[role] || ROLE_DEFAULT_SECTIONS.RECEPTIONIST
          await createDashboardFromCategories({ roleSections: defaultSections })
        }
        if (Array.isArray(result?.sections) && typeof result?.llm_enabled === 'boolean') {
          llmEnabled.value = result.llm_enabled
          try {
            localStorage.setItem('dashboard-llm-enabled', String(result.llm_enabled))
          } catch {}
        }
        initialLoading.value = false

        // Fetch best practices data for automations footer
        fetchBestPractices({
          getDemoData: trialMode.value ? getDemoBestPracticesData : null
        })
      } catch (storageError) {
        console.error('Error loading dashboard configuration:', storageError)
        // Show empty state if loading fails
        sections.value = []
        lastSavedSections.value = []
        initialLoading.value = false
      }
    }
    
    // Listen for token received via postMessage (when opened in new tab)
    const handleTokenReceived = async () => {
      const newValidation = validateIframeParams()
      if (newValidation.token) {
        token.value = newValidation.token
        orgId.value = newValidation.orgId
        tokenError.value = false // Clear error state when token is received
        
        // Load dashboard data now that we have the token
        await loadDashboardData()
      }
    }
    window.addEventListener('token-received', handleTokenReceived)
    
    // Store handler for cleanup
    window._dashboardTokenHandler = handleTokenReceived
    
    // Also check for token after a short delay (in case postMessage arrives late)
    setTimeout(() => {
      const delayedToken = getTokenFromURL()
      if (delayedToken && !token.value) {
        token.value = delayedToken
        // Load dashboard if we got the token
        if (waitingForToken) {
          loadDashboardData()
        }
      } else if (waitingForToken) {
        // Still waiting for token, set a timeout to stop loading after max wait time
        setTimeout(() => {
          if (initialLoading.value && !token.value) {
            console.warn('Token not received after timeout, showing error state')
            initialLoading.value = false
            tokenError.value = true
            sections.value = []
            lastSavedSections.value = []
          }
        }, 5000) // Max 5 seconds wait
      }
    }, 200)
    
    // If we have a token or we're not in fullscreen mode, load dashboard immediately
    if (!waitingForToken) {
      await loadDashboardData()
    } else {
      // If waiting for token in fullscreen mode, keep loading state
      // The token will arrive via postMessage and trigger loadDashboardData
    }
    
    // After widgets are mounted, flush the batch queue to ensure data loads quickly
    // This allows widgets to register their data needs, then we batch and fetch them all
    await nextTick()
    setTimeout(() => {
      dataFetcher.flushBatch()
    }, 100) // Small delay to ensure all widgets have registered

    // In panel mode (Optix side panel): read params from URL and request parent size
    if (urlParams.get('panel') === 'add-widget' && isInOptixIframe()) {
      const sectionIdFromUrl = urlParams.get('sectionId')
      if (sectionIdFromUrl) {
        addWidgetPanelTargetSectionId.value = sectionIdFromUrl
        // If the target section wasn't loaded from storage (e.g. new empty section),
        // create it so widgets can be added to it
        if (!sections.value.find(s => s.id === sectionIdFromUrl)) {
          sections.value = [...sections.value, {
            id: sectionIdFromUrl,
            title: 'New Section',
            expanded: true,
            displayMode: 'detailed',
            widgets: []
          }]
        }
      }
      setTimeout(() => requestParentSize(), 300)
    }
    if (urlParams.get('panel') === 'widget-settings' && isInOptixIframe()) {
      setTimeout(() => requestParentSize(), 300)
    }
    if (urlParams.get('panel') === 'widget-detail' && isInOptixIframe()) {
      setTimeout(() => requestParentSize(), 300)
    }
    if ((urlParams.get('panel') === 'view-all-actions' || urlParams.get('panel') === 'view-all-time-saved' || urlParams.get('panel') === 'view-all-top-bookers' || urlParams.get('panel') === 'view-all-revenue-collected' || urlParams.get('panel') === 'view-all-support-chat') && isInOptixIframe()) {
      setTimeout(() => requestParentSize(), 300)
    }
    
  } catch (err) {
    console.error('Dashboard initialization error:', err)
    // Don't block dashboard from loading on errors
    initialLoading.value = false
  }
})

// -- Section drag-to-reorder via SortableJS --
let sectionSortable = null

watch(editMode, (isEdit) => {
  if (isEdit) {
    nextTick(() => {
      if (sectionsListRef.value && !sectionSortable) {
        sectionSortable = Sortable.create(sectionsListRef.value, {
          handle: '.drag-handle',
          ghostClass: 'section-ghost',
          animation: 200,
          forceFallback: true,
          fallbackClass: 'section-fallback',
          onEnd(evt) {
            if (evt.oldIndex !== evt.newIndex) {
              const newSections = [...sections.value]
              const [moved] = newSections.splice(evt.oldIndex, 1)
              newSections.splice(evt.newIndex, 0, moved)
              sections.value = newSections
            }
          }
        })
      }
    })
  } else if (sectionSortable) {
    sectionSortable.destroy()
    sectionSortable = null
  }
})

onUnmounted(() => {
  if (sectionSortable) {
    sectionSortable.destroy()
    sectionSortable = null
  }
  if (editTipScrollResizeCleanup) {
    editTipScrollResizeCleanup()
    editTipScrollResizeCleanup = null
  }
  if (emptyStateDelayTimer) {
    clearTimeout(emptyStateDelayTimer)
    emptyStateDelayTimer = null
  }
  if (loadingSkeletonTimer) {
    clearTimeout(loadingSkeletonTimer)
    loadingSkeletonTimer = null
  }
  if (headerResizeObserver && dashboardHeaderRef.value) {
    headerResizeObserver.disconnect()
  }
  window.removeEventListener('resize', handleResize)
  if (dashboardContentRef.value && window._dashboardScrollChevronHandler) {
    dashboardContentRef.value.removeEventListener('scroll', window._dashboardScrollChevronHandler)
  }
  delete window._dashboardScrollChevronHandler
  if (window._dashboardTokenHandler) {
    window.removeEventListener('token-received', window._dashboardTokenHandler)
    delete window._dashboardTokenHandler
  }
  try {
    if (window._dashboardBroadcastChannel) {
      window._dashboardBroadcastChannel.removeEventListener('message', window._dashboardBroadcastHandler)
      window._dashboardBroadcastChannel.close()
      delete window._dashboardBroadcastChannel
      delete window._dashboardBroadcastHandler
    }
  } catch (_e) {}
})
</script>

<style scoped>
.dashboard {
  padding: 20px 20px 0 20px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 1);
  overflow: hidden;
}

.dashboard--llm {
  background-color: rgba(250, 250, 250, 1);
  border-top: none;
  border-right: none;
  border-bottom: none;
  border-left: 1px solid rgba(230, 230, 230, 1);
}

.dashboard.panel-mode {
  padding: 0;
  border: none;
}

.panel-mode-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
}

.panel-mode-container--iframe {
  padding: 0;
}

.inbox-conversation-panel {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.inbox-conversation-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.inbox-conversation-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

.inbox-conversation-iframe {
  width: 100%;
  height: 100%;
  min-height: 500px;
  border: none;
  display: block;
}

.panel-loading {
  padding: 20px;
}

.dashboard-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 60px;
  padding-top: 140px;
  padding-bottom: 20px;
  padding-left: 20px;
  padding-right: 20px;
  border-bottom-width: 0px;
  border-bottom-color: rgba(0, 0, 0, 0);
  border-bottom-style: none;
  border-image: none;
}

.dashboard-header--llm {
  padding-top: 210px;
}

.fullscreen-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 24px;
}

.fullscreen-header-avatar {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
}

.fullscreen-header-avatar--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  color: #374151;
  font-size: 20px;
  font-weight: 600;
}

.fullscreen-header-name {
  font-size: 32px;
  font-weight: 500;
  color: #111827;
}

.header-top {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  margin-top: -6px;
}

.trial-mode-pill-wrapper {
  position: relative;
  display: inline-flex;
  margin-top: 28px;
}

.trial-mode-pill-wrapper:hover .trial-mode-pill-tooltip {
  opacity: 1;
}

.trial-mode-pill-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;
}

.trial-mode-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #553a2a;
  background: #fff8d1;
  border: none;
  border-radius: 4px;
  letter-spacing: 0.01em;
}

.header-title {
  flex: 0 0 auto;
  font-size: 2.5rem;
  color: #333;
  margin: 0;
  font-weight: 500;
  text-align: center;
  letter-spacing: -0.02em;
}

.header-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  z-index: 10001;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.dashboard--llm .header-backdrop {
  background: rgba(250, 250, 250, 0.55);
}

.header-left {
  position: fixed;
  top: 10px;
  left: 20px;
  display: flex;
  align-items: center;
  z-index: 10002;
  background: white;
  padding: 8px 12px;
  border-radius: 8px;
}

.dashboard--llm .header-left {
  background: rgba(250, 250, 250, 1);
}

.dashboard--editing .header-left,
.dashboard--editing.dashboard--llm .header-left {
  background: transparent;
}

.burger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.burger-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #333;
}

.burger-btn:active {
  background: rgba(0, 0, 0, 0.1);
}

.burger-icon {
  width: 24px;
  height: 24px;
}

.header-right {
  position: fixed;
  top: 10px;
  right: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  z-index: 10002;
  background: white;
  padding: 8px 12px;
  border-radius: 8px;
}

.dashboard--llm .header-right {
  background: rgba(250, 250, 250, 1);
}

.dashboard--editing .header-right,
.dashboard--editing.dashboard--llm .header-right {
  background: transparent;
}

.dashboard-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin: 0;
  font-weight: 500;
  letter-spacing: -0.02em;
}

.header-bottom {
  width: 100%;
  text-align: center;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 8px 0;
  line-height: 1.5;
}

/* Ask Optix anything - Claude-style input */
.optix-prompt-wrapper {
  width: 100%;
  max-width: 660px;
  margin: 56px auto 0;
  padding-bottom: 56px;
}

.optix-prompt {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.optix-prompt-input {
  width: 100%;
  min-height: 72px;
  padding: 16px 16px 8px 16px;
  border: none;
  outline: none;
  resize: none;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  color: #333;
  background: transparent;
  font-family: inherit;
}

.optix-prompt-input::placeholder {
  color: #6b7280;
}

.optix-prompt-input:focus {
  outline: none;
}

.optix-prompt-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px 12px 12px;
}

.optix-prompt-attach {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
}

.optix-prompt-attach:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
}

.optix-prompt-model {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.optix-prompt-send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-left: auto;
  padding: 0;
  border: none;
  background: #e8d5cf;
  color: #6b4c3d;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.optix-prompt-send:hover {
  background: #dfc9c1;
  color: #5a4032;
}

.optix-prompt-send:active {
  background: #d5bdb5;
}

.optix-prompt-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}

.optix-suggestion-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.optix-suggestion-btn:hover {
  border-color: rgba(0, 0, 0, 0.2);
  background: #fafafa;
}

.optix-suggestion-btn:active {
  background: #f5f5f5;
}

.dashboard-links {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 8px;
  align-items: center;
  justify-content: center;
}

.dashboard-link-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dashboard-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2383E2;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s;
  white-space: normal;
  word-wrap: break-word;
  max-width: 100%;
}

.link-text-desktop {
  display: inline;
}

.link-text-mobile {
  display: none;
}

.dashboard-link:hover {
  color: #1d6bc4;
}

.link-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #2383E2;
}

.org-info {
  display: none;
  padding: 8px 16px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.87);
}

.save-indicator {
  padding: 8px 12px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.87);
  font-style: italic;
}

/* Trial mode toggle - Material Design switch style */
.trial-mode-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
}

.trial-mode-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.trial-mode-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.trial-mode-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
}

.trial-mode-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
}

.trial-mode-track {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  border-radius: 10px;
  transition: background 0.2s, opacity 0.2s;
}

.trial-mode-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.trial-mode-input:checked + .trial-mode-track {
  background: #2383E2;
  opacity: 1;
}

.trial-mode-input:checked + .trial-mode-track + .trial-mode-thumb {
  transform: translateX(16px);
}

.trial-mode-toggle--on .trial-mode-track {
  background: #2383E2;
  opacity: 1;
}

.trial-mode-toggle--on .trial-mode-thumb {
  transform: translateX(16px);
}

.trial-mode-tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-size: 12px;
  display: block;
  width: max-content;
  max-width: 280px;
  text-align: center;
  border-radius: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;
}

.trial-mode-wrapper:hover .trial-mode-tooltip {
  opacity: 1;
}

.open-new-tab-wrapper {
  position: relative;
  display: inline-block;
  margin-right: -8px;
}

.open-new-tab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
}

.open-new-tab-btn:focus {
  outline: none;
  box-shadow: none;
}

.open-new-tab-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.6);
}

.open-new-tab-btn:active {
  background: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.6);
}

.open-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.open-new-tab-tooltip {
  position: fixed;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10010;
  overflow: visible;
}

.add-section-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 112px;
  margin: 32px 20px 16px 20px;
  padding: 16px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
  color: #2383E2;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.add-section-zone span {
  margin-top: 2px;
}

.add-section-zone .add-section-icon {
  color: #2383e2;
}

.add-section-zone:hover {
  border-color: #2383E2;
  background: rgba(35, 131, 226, 0.05);
  color: #2383E2;
}

.edit-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

.settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  padding: 0;
  background-color: #fff;
  color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
}

.settings-btn:focus {
  outline: none;
  box-shadow: none;
}

.settings-btn:hover {
  background: #f9fafb;
  color: rgba(0, 0, 0, 0.87);
}

.settings-btn:active {
  background: #f3f4f6;
}

.settings-btn-icon {
  width: 18px;
  height: 18px;
}

.settings-panel-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 460px;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  /* Above the edit-mode header controls (z-index 10010) so the panel is interactive */
  z-index: 10020;
  overflow-y: auto;
}

.llm-mode-toggle {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-right: 16px;
}

.llm-mode-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.llm-mode-switch {
  position: relative;
  width: 36px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.26);
  cursor: pointer;
  transition: background-color 0.2s;
  outline: none;
}

.llm-mode-switch:hover {
  background: rgba(0, 0, 0, 0.38);
}

.llm-mode-switch--on {
  background: rgba(35, 131, 226, 1);
}

.llm-mode-switch--on:hover {
  background: #1d6bc4;
}

.llm-mode-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.llm-mode-switch--on .llm-mode-switch-thumb {
  transform: translateX(16px);
}

.edit-dashboard-wrapper {
  position: relative;
  display: inline-block;
  margin-left: 8px;
}

.edit-dashboard-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 88px;
  padding: 0 16px;
  background-color: transparent;
  color: rgba(0, 0, 0, 0.87);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  outline: none;
}

.edit-dashboard-btn:focus {
  outline: none;
  box-shadow: none;
}

.edit-dashboard-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

.edit-dashboard-btn:active {
  background: rgba(0, 0, 0, 0.1);
}

.edit-dashboard-text {
  display: block;
}

.edit-dashboard-icon {
  display: none;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.edit-dashboard-tooltip {
  position: fixed;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10010;
  overflow: visible;
}

/* Edit tip callout - matches black hover tooltip style (edit-dashboard-tooltip, open-new-tab-tooltip) */
.edit-tip-callout {
  position: fixed;
  max-width: 280px;
  padding: 16px 20px;
  background: rgba(0, 0, 0, 1);
  color: white;
  font-size: 12px;
  border-radius: 4px;
  z-index: 10010;
  overflow: visible;
}

/* Tour pointer - arrow pointing right at Edit button (only tooltip that keeps an arrow) */
.edit-tip-callout::after {
  content: '';
  position: absolute;
  left: 100%;
  top: var(--edit-tip-arrow-top, 50%);
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-left-color: rgba(0, 0, 0, 1);
  pointer-events: none;
}

.edit-tip-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin: 0 0 4px 0;
}

.edit-tip-message {
  font-size: 12px;
  line-height: 1.5;
  color: white;
  margin: 0 0 12px 0;
}

.edit-tip-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.edit-tip-edit-btn {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  background: rgba(35, 131, 226, 1);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.edit-tip-edit-btn:hover {
  background: rgba(28, 105, 181, 1);
}

.edit-tip-got-it-link {
  padding: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
}

.edit-tip-got-it-link:hover {
  color: white;
}

.edit-tip-fade-enter-active,
.edit-tip-fade-leave-active {
  transition: opacity 0.2s ease;
}
.edit-tip-fade-enter-from,
.edit-tip-fade-leave-to {
  opacity: 0;
}

.cancel-btn {
  height: 40px;
  min-width: 88px;
  padding: 0 16px;
  background-color: #fff;
  color: rgba(0, 0, 0, 0.87);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
}

.cancel-btn:focus {
  outline: none;
  box-shadow: none;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.cancel-btn:active {
  background: #f3f4f6;
}

.save-btn {
  height: 40px;
  min-width: 88px;
  padding: 0 16px;
  background-color: rgba(35, 131, 226, 1);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  outline: none;
}

.save-btn:focus {
  outline: none;
  box-shadow: none;
}

.save-btn:hover {
  background: #1d6bc4;
}

.save-btn:active {
  background: #1759a6;
}

.dashboard-content-wrapper {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.scroll-fade-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(to top, rgba(250, 250, 250, 1), transparent);
  pointer-events: none;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.scroll-fade-overlay--hidden {
  opacity: 0;
  visibility: hidden;
}

.scroll-chevron {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;
}

.scroll-chevron--hidden {
  opacity: 0;
  visibility: hidden;
}

.sections-spacer {
  flex-shrink: 0;
}

.loading-sections {
  width: 100%;
  padding: 20px 0;
}

.error {
  width: 100%;
  text-align: center;
  padding: 40px 20px;
  font-size: 1.2rem;
}

.error {
  color: #d32f2f;
}

.dashboard-content {
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 20px;
  transition: padding-bottom 0.3s ease;
}

.dashboard-content.panel-open {
  padding-right: 1020px;
}

.sections-container {
  display: flex;
  flex-direction: column;
  gap: 48px;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}

.sections-list {
  display: flex;
  flex-direction: column;
  gap: 48px;
}

.section-ghost {
  opacity: 0.3;
}

.section-fallback {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  opacity: 0.9;
}

.no-widgets {
  text-align: center;
  padding: 40px;
  color: rgba(0, 0, 0, 0.87);
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 60vh;
  padding: 80px 20px 40px;
  box-sizing: border-box;
  contain: layout;
}

.empty-state-skeleton {
  max-width: 1280px;
  width: 100%;
}

.empty-state-content {
  max-width: 800px;
  width: 100%;
  text-align: center;
  margin: 0 auto;
  transition: opacity 0.15s ease;
}

.empty-state-content--delayed {
  opacity: 0;
}

.empty-state-title {
  font-size: 2.5rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
}

.empty-state-description {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 auto 48px;
  line-height: 1.5;
  max-width: 600px;
}

.empty-state-categories {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 480px;
  margin: 0 auto 32px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.empty-state-category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state-category-item {
  transition: transform 0.2s;
}

.empty-state-category-item--ghost {
  opacity: 0.4;
}

.empty-state-category-item--drag {
  opacity: 0.9;
}

.empty-state-category-btn {
  display: flex;
  align-items: center;
  width: 100%;
  height: 72px;
  min-height: 72px;
  padding: 0 24px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-align: left;
  outline: none;
  box-sizing: border-box;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
}

.empty-state-category-btn--locked {
  cursor: default;
  overflow: visible;
}

.empty-state-category-btn--locked .category-btn-check {
  opacity: 0.5;
}

.category-btn-check-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
}

.category-btn-check-wrapper:hover .category-btn-locked-tooltip {
  opacity: 1;
  pointer-events: auto;
}

.category-btn-locked-tooltip {
  position: absolute;
  top: -36px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a1a;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
  z-index: 10;
}

.category-btn-locked-tooltip::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #1a1a1a;
}

.category-btn-drag-handle {
  display: flex;
  align-items: center;
  margin-right: 12px;
  color: #999;
  cursor: grab;
  flex-shrink: 0;
  transition: opacity 0.2s;
}

.category-btn-drag-handle:active {
  cursor: grabbing;
}

.category-btn-drag-handle--disabled {
  opacity: 0.3;
  pointer-events: none;
  cursor: default;
}

.category-btn-title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.category-btn-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  max-height: 24px;
  font-size: 20px;
  line-height: 1;
  border-radius: 50%;
  background: rgba(35, 131, 226, 1);
  color: #fff;
  flex-shrink: 0;
  margin-left: auto;
}

.category-btn-check--empty {
  background: transparent;
  border: 2px solid #d1d5db;
}

.empty-state-category-btn:hover {
  border-color: #2383E2;
}

.empty-state-category-btn--selected {
  border-color: rgba(35, 131, 226, 1);
  background: rgba(35, 131, 226, 0.06);
}

.empty-state-category-btn--selected:hover {
  border-color: #2383E2;
  background: rgba(35, 131, 226, 0.08);
}

.empty-state-category-btn:focus,
.empty-state-category-btn:focus-visible,
.empty-state-category-btn--selected:focus,
.empty-state-category-btn--selected:focus-visible {
  outline: none;
  outline-offset: 0;
  box-shadow: none;
}

.empty-state-category-btn:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.category-btn-count {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  margin-left: auto;
  margin-right: 12px;
}

.empty-state-create-btn {
  display: block;
  width: 100%;
  margin: 24px 0 0;
  padding: 14px 40px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: rgba(35, 131, 226, 1);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.empty-state-create-btn:hover:not(:disabled) {
  background: rgba(28, 105, 181, 1);
  transform: translateY(-1px);
}

.empty-state-create-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .optix-prompt-wrapper {
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
  
  .dashboard {
    padding: 16px 16px 0 16px;
  }
  
  .add-section-zone {
    margin-left: 0;
    margin-right: 0;
  }
  
  .dashboard-header {
    padding-left: 0;
  }

  .header-top {
    margin-bottom: 16px;
  }
  
  .header-bottom {
    width: 100%;
  }
  
  .dashboard-subtitle {
    width: 100%;
    max-width: 100%;
  }
  
  .dashboard-links {
    width: 100%;
    gap: 16px;
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
  }
  
  .dashboard-link {
    font-size: 0.85rem;
    width: auto;
    white-space: nowrap;
  }
  
  .link-text-desktop {
    display: none;
  }
  
  .link-text-mobile {
    display: inline;
  }
  
  .header-left,
  .dashboard--llm .header-left {
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 56px;
    padding: 0 16px;
    border-radius: 0;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-sizing: border-box;
  }

  .header-right,
  .dashboard--llm .header-right {
    top: 0;
    right: 0;
    height: 56px;
    padding: 0 16px;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 0;
  }
  
  .edit-dashboard-btn {
    width: 40px;
    height: 40px;
    padding: 0;
    background: transparent;
    color: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 50%;
  }
  
  .edit-dashboard-btn:hover {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.6);
  }
  
  .edit-dashboard-btn:active {
    background: rgba(0, 0, 0, 0.1);
    color: rgba(0, 0, 0, 0.6);
  }
  
  .edit-dashboard-text {
    display: none;
  }
  
  .edit-dashboard-icon {
    display: block;
  }
}

/* Global Snackbar styles */
.snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  background: rgba(0, 0, 0, 0.87);
  color: white;
  font-size: 14px;
  white-space: nowrap;
  border-radius: 4px;
  pointer-events: auto;
  z-index: 10003;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  gap: 16px;
}

.snackbar-message {
  flex: 1;
}

.snackbar-undo {
  background: transparent;
  border: none;
  color: #90caf9;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.2s;
}

.snackbar-undo:hover {
  opacity: 0.8;
}

/* Snackbar animation */
.snackbar-enter-active,
.snackbar-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.snackbar-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.snackbar-enter-to {
  transform: translateX(-50%) translateY(0);
}

.snackbar-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

/* Wiggle animation when snackbar re-triggered while already visible */
.snackbar-wiggle {
  animation: snackbar-wiggle 0.4s ease-in-out;
}

@keyframes snackbar-wiggle {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  20% { transform: translateX(calc(-50% - 6px)) translateY(0); }
  40% { transform: translateX(calc(-50% + 6px)) translateY(0); }
  60% { transform: translateX(calc(-50% - 3px)) translateY(0); }
  80% { transform: translateX(calc(-50% + 3px)) translateY(0); }
}

/* Error state for missing token in fullscreen mode */
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  width: 100%;
}

.error-state-content {
  max-width: 500px;
  width: 100%;
  text-align: center;
}

.error-icon {
  width: 64px;
  height: 64px;
  color: #d32f2f;
  margin: 0 auto 24px;
  display: block;
}

.error-state-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
}

.error-state-description {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
  margin: 0 0 12px 0;
  line-height: 1.6;
}

.error-state-description-secondary {
  font-size: 0.9rem;
  color: #888;
  margin: 0;
  line-height: 1.5;
}

/* Rate limit error (429) styling */
.rate-limit-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  padding: 40px 20px;
  width: 100%;
}

.rate-limit-error-content {
  max-width: 400px;
  width: 100%;
  text-align: center;
}

.rate-limit-icon {
  width: 64px;
  height: 64px;
  color: #ff9800;
  margin: 0 auto 24px;
  display: block;
}

.rate-limit-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.rate-limit-description {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
  line-height: 1.6;
}
</style>
